"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import React from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import * as XLSX from "xlsx";
import { PDFDocument } from "pdf-lib";
import { jwtDecode } from "jwt-decode";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import EditChangeOrderExcelUploadModal from "./EditChangeOrderExcelUploadModal";
import CORevisionPanel from "./CORevisionPanel";
import COAmendmentPanel from "./COAmendmentPanel";
import ImageModule from "docxtemplater-image-module-free";
import { saveAs } from "file-saver";
import {
  ArrowLeftIcon,
  DocumentArrowDownIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/20/solid";
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import AlertDialog from "./AlertDialog";
import BudgetItemsModal from "./ChangeOrderBudgetItemsModal";
import DatabaseItemsModal from "./ChangeOrderDatabaseItemsModal";
import TandMCostPanel from "./TandMCostPanel";

// Sub-components split out for size
import COBudgetItemsSection from "./COBudgetItemsSection";
import COPricingSections from "./COPricingSections";

const API_BASE = "https://erp-xe9u.onrender.com/api";
const DEFAULT_MARKUP = 30;

export const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#4b5563",
    borderColor: state.isFocused ? "#f97316" : "#6b7280",
    color: "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #f97316" : "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "38px",
    fontSize: "12px",
  }),
  input: (p) => ({ ...p, color: "#e5e7eb" }),
  singleValue: (p) => ({ ...p, fontSize: "12px", color: "#e5e7eb" }),
  option: (p, state) => ({
    ...p,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#6b7280" },
    fontSize: "12px",
  }),
  menu: (p) => ({
    ...p,
    backgroundColor: "#374151",
    border: "1px solid #4b5563",
    zIndex: 9999,
  }),
  menuPortal: (p) => ({ ...p, zIndex: 999999 }),
  placeholder: (p) => ({ ...p, color: "#9ca3af" }),
};

export const formatCurrency = (num) =>
  Number.isFinite(num)
    ? `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "$0.00";

const formatCurrencyForPDF = (value) => {
  const absValue = Math.abs(value);
  const formatted = absValue.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `(${formatted})` : formatted;
};

// ── Confirmation Modal ────────────────────────────────────────────────────────
export const ConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  confirmColor,
}) => (
  <Dialog open={open} onClose={onClose} className="relative z-50">
    <DialogBackdrop className="fixed inset-0 bg-gray-900/75 transition-opacity" />
    <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <DialogPanel className="relative transform overflow-hidden rounded-lg bg-gray-700 px-4 pb-4 pt-5 shadow-xl sm:max-w-lg sm:p-6 border border-gray-600">
          <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md text-gray-400 hover:text-gray-200"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex size-10 shrink-0 items-center justify-center rounded-full bg-gray-600 sm:mx-0">
              <ExclamationTriangleIcon className="size-6 text-orange-400" />
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
              <DialogTitle
                as="h3"
                className="text-lg font-semibold text-orange-400"
              >
                {title}
              </DialogTitle>
              <div className="mt-2">
                <p className="text-sm text-gray-200">{message}</p>
              </div>
            </div>
          </div>
          <div className="mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
            <button
              type="button"
              onClick={onConfirm}
              className={`inline-flex w-full justify-center rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto ${confirmColor === "green" ? "bg-green-600 hover:bg-green-700" : confirmColor === "red" ? "bg-red-500 hover:bg-red-600" : "bg-orange-500 hover:bg-orange-600"}`}
            >
              {confirmText}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex w-full justify-center rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </DialogPanel>
      </div>
    </div>
  </Dialog>
);

// ── Email Modal ───────────────────────────────────────────────────────────────
const EmailModal = ({
  open,
  onClose,
  emailForm,
  setEmailForm,
  onSend,
  userEmail,
  isSending,
  contacts = [],
  loadingContacts = false,
}) => {
  if (!open) return null;
  const contactOptions = contacts.map((c) => ({
    value: c._id,
    label: `${c.firstName || ""} ${c.lastName || ""}`.trim() || "Unnamed",
    email: c.email,
  }));
  const selectedContacts = emailForm.toContacts || [];
  const handleContactChange = (selected) => {
    const s = selected || [];
    setEmailForm({
      ...emailForm,
      toContacts: s,
      to: s
        .map((o) => o.email)
        .filter(Boolean)
        .join(", "),
    });
  };
  return (
    <div className="fixed inset-0 bg-gray-900/75 flex items-center justify-center z-50">
      <div className="bg-gray-700 p-6 rounded-lg shadow-xl w-full max-w-lg border border-gray-600">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-orange-400">
            Send Change Order Email
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-200">
              From
            </label>
            <input
              type="email"
              value={userEmail}
              disabled
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-400 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-200">
              Select Recipients
            </label>
            <Select
              isMulti
              options={contactOptions}
              value={selectedContacts}
              onChange={handleContactChange}
              placeholder={
                loadingContacts ? "Loading..." : "Select contacts..."
              }
              isLoading={loadingContacts}
              className="mt-1"
              styles={customStyles}
              menuPortalTarget={document.body}
              noOptionsMessage={() => "No contacts found"}
              formatOptionLabel={(o) => (
                <div className="flex justify-between items-center">
                  <span>{o.label}</span>
                  {o.email && (
                    <span className="text-xs text-gray-400 ml-2">
                      {o.email}
                    </span>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-200">
              To (Email Addresses)
            </label>
            <input
              type="text"
              value={emailForm.to}
              onChange={(e) =>
                setEmailForm({ ...emailForm, to: e.target.value })
              }
              placeholder="email@example.com"
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-200">
              Subject
            </label>
            <input
              type="text"
              value={emailForm.subject}
              onChange={(e) =>
                setEmailForm({ ...emailForm, subject: e.target.value })
              }
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-200">
              Body
            </label>
            <textarea
              value={emailForm.body}
              onChange={(e) =>
                setEmailForm({ ...emailForm, body: e.target.value })
              }
              rows="5"
              className="mt-1 block w-full rounded-md bg-gray-600 border-gray-500 text-gray-200 text-sm"
            />
          </div>
          {selectedContacts.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-3 border border-gray-600">
              <p className="text-xs text-gray-400 mb-2">
                Recipients ({selectedContacts.length}):
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedContacts.map((c) => (
                  <span
                    key={c.value}
                    className="inline-flex items-center px-2 py-1 rounded text-xs bg-orange-500/20 text-orange-300 border border-orange-500/30"
                  >
                    {c.label}
                    {c.email && (
                      <span className="ml-1 text-orange-400/70">
                        ({c.email})
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-600">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={isSending || !emailForm.to}
              className={`px-4 py-2 text-sm text-white rounded-md flex items-center ${isSending || !emailForm.to ? "bg-gray-500 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
            >
              <EnvelopeIcon className="h-4 w-4 mr-2" />
              {isSending ? "Sending..." : "Send Email"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function EditChangeOrderFormV3() {
  const { id: projectId, changeOrderId } = useParams();
  const navigate = useNavigate();

  // ── Form state ──────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    title: "",
    customerDescription: "",
    scopeOfWork: "",
    referenceNumber: "",
    termsAndConditions: "Original terms and conditions.",
    overheadPercentage: 0,
    defaultMarkupPercentage: DEFAULT_MARKUP,
    changesToSchedule: 0,
    approvalStatus: "Pending",
  });
  const [summary, setSummary] = useState({
    description: "",
    qtyUnit: "1",
    billable: true,
    revision: 0,
  });
  const [changeItems, setChangeItems] = useState([]);
  const [pricingSections, setPricingSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [sectionActiveTab, setSectionActiveTab] = useState({});

  // ── T&M linking ─────────────────────────────────────────────────────────────
  const [tandmCostData, setTandmCostData] = useState(null);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  // ── UI state ────────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [budgetItemsCollapsed, setBudgetItemsCollapsed] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState("details");
  const [viewingRevision, setViewingRevision] = useState(null);
  const [addingToSection, setAddingToSection] = useState(null);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // ── Filter/sort state ───────────────────────────────────────────────────────
  const [changeOrderFilters, setChangeOrderFilters] = useState({
    itemCode: [],
    description: "",
    category: [],
    condition: [],
    bidArea: [],
    size: [],
    uom: [],
    reference: [],
    laborId: [],
  });
  const [showCOFilter, setShowCOFilter] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  // ── Reference data ──────────────────────────────────────────────────────────
  const [vendors, setVendors] = useState([]);
  const [project, setProject] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [savedChangeOrder, setSavedChangeOrder] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedAttentionTo, setSelectedAttentionTo] = useState([]);
  const [amendments, setAmendments] = useState([]);

  // ── Budget items ─────────────────────────────────────────────────────────────
  const [budgetItems, setBudgetItems] = useState([]);
  const [loadingBudgetItems, setLoadingBudgetItems] = useState(false);
  const [selectedBudgetItems, setSelectedBudgetItems] = useState([]);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [netQuantities, setNetQuantities] = useState(new Map());
  const [netLoading, setNetLoading] = useState(false);

  // ── Database items ───────────────────────────────────────────────────────────
  const [databaseItems, setDatabaseItems] = useState([]);
  const [loadingDatabaseItems, setLoadingDatabaseItems] = useState(false);
  const [selectedDatabaseItems, setSelectedDatabaseItems] = useState([]);
  const [showDatabaseModal, setShowDatabaseModal] = useState(false);
  const [databaseFilters, setDatabaseFilters] = useState({
    category: [],
    uom: [],
    search: "",
  });

  // ── Email ────────────────────────────────────────────────────────────────────
  const [emailForm, setEmailForm] = useState({
    to: "",
    subject: "",
    body: "",
    signature: "",
  });
  const [userEmail, setUserEmail] = useState("");
  const [pdfBuffer, setPdfBuffer] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // ── Alert ────────────────────────────────────────────────────────────────────
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => setAlert((p) => ({ ...p, open: false })),
  });

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  // ── Derived flags ────────────────────────────────────────────────────────────
  const wasApproved = savedChangeOrder?.approvalStatus === "Approved";
  const wasRejected = savedChangeOrder?.approvalStatus === "Rejected";
  const isPending = formData.approvalStatus === "Pending";
  const isReadOnly = !!viewingRevision;
  const isTandMBased = !!savedChangeOrder?.isTandMBased;
  const tandmId = savedChangeOrder?.linkedTandMId?._id
    ? savedChangeOrder.linkedTandMId._id.toString()
    : savedChangeOrder?.linkedTandMId?.toString() || null;
  const tandmNumber =
    savedChangeOrder?.linkedTandMId?.timeAndMaterialNumber || null;

  // ── Filter refs ──────────────────────────────────────────────────────────────
  const coFilterRefs = {
    itemCode: useRef(null),
    description: useRef(null),
    category: useRef(null),
    condition: useRef(null),
    bidArea: useRef(null),
    size: useRef(null),
    uom: useRef(null),
    reference: useRef(null),
    laborId: useRef(null),
  };

  const showAlert = (type, title, message, onConfirm) => {
    setAlert({
      open: true,
      type,
      title,
      message,
      onConfirm: onConfirm || (() => setAlert((p) => ({ ...p, open: false }))),
    });
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const isSFUnit = (uom) => {
    const u = (uom || "").toLowerCase();
    return (
      u === "sf" || u.includes("square") || u.includes("sq ft") || u === "sheet"
    );
  };
  const isLFUnit = (uom) => {
    const u = (uom || "").toLowerCase();
    return u === "lf" || u.includes("linear") || u.includes("lin ft");
  };
  const isFromBudget = (item) =>
    !!item.sourceAssemblyId || !!item.sourceLineItemId;

  const parseSizeToCoverage = (sizeStr, uom) => {
    if (!sizeStr) return null;
    const str = String(sizeStr).trim().toLowerCase();
    const parseLength = (part) => {
      if (!part) return 0;
      const m = part.match(/^(\d+(?:\.\d+)?)' ?(\d+(?:\.\d+)?)"?$/);
      if (m) return parseFloat(m[1]) + parseFloat(m[2]) / 12;
      const f = part.match(/^([\d.]+)'?$/);
      if (f) return parseFloat(f[1]) || 0;
      const i = part.match(/^([\d.]+)"$/);
      if (i) return (parseFloat(i[1]) || 0) / 12;
      const n = parseFloat(part);
      return isNaN(n) ? 0 : n;
    };
    if (isSFUnit(uom)) {
      const parts = str.split(/\s*[xX×]\s*/);
      if (parts.length === 2) {
        const w = parseLength(parts[0]);
        const h = parseLength(parts[1]);
        if (w > 0 && h > 0) return w * h;
      }
      const a = parseLength(parts[0]);
      return a > 0 ? a : null;
    }
    if (isLFUnit(uom)) {
      const l = parseLength(str);
      return l > 0 ? l : null;
    }
    return null;
  };

  // ── Memos ────────────────────────────────────────────────────────────────────
  const referenceOptions = useMemo(
    () => [
      { value: null, label: "— None —" },
      ...pricingSections.map((s) => ({
        value: s.number,
        label: `${s.number} - ${s.title || "Untitled"}`,
      })),
    ],
    [pricingSections],
  );

  const assemblyOptions = useMemo(() => {
    const unique = new Set();
    budgetItems.forEach((i) => {
      if (i.conditionName) unique.add(i.conditionName);
    });
    changeItems.forEach((i) => {
      if (i.conditionName) unique.add(i.conditionName);
    });
    return [
      { value: "", label: "— Select Assembly —" },
      ...Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v })),
    ];
  }, [budgetItems, changeItems]);

  const bidAreaOptions = useMemo(() => {
    const unique = new Set();
    budgetItems.forEach((i) => {
      if (i.bidArea) unique.add(i.bidArea);
    });
    changeItems.forEach((i) => {
      if (i.bidArea) unique.add(i.bidArea);
    });
    return [
      { value: "", label: "— Select Bid Area —" },
      ...Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v })),
    ];
  }, [budgetItems, changeItems]);

  const laborIdsByCondition = useMemo(() => {
    const map = new Map();
    [...budgetItems, ...changeItems].forEach((item) => {
      if (item.conditionName && item.laborId) {
        if (!map.has(item.conditionName))
          map.set(item.conditionName, new Set());
        map.get(item.conditionName).add(item.laborId);
      }
    });
    const result = {};
    map.forEach((set, key) => {
      result[key] = Array.from(set).sort(
        (a, b) =>
          (parseInt(a.replace(/\D/g, "")) || 0) -
          (parseInt(b.replace(/\D/g, "")) || 0),
      );
    });
    return result;
  }, [budgetItems, changeItems]);

  const getLaborIdOptions = useCallback(
    (conditionName) => {
      if (!conditionName) return [];
      const existing = laborIdsByCondition[conditionName] || [];
      const options = existing.map((id) => ({
        value: id,
        label: id,
        isExisting: true,
      }));
      const numbers = existing
        .map((id) => parseInt(id.replace(/^L/, "")) || 0)
        .filter((n) => !isNaN(n));
      const nextId = `L${numbers.length > 0 ? Math.max(...numbers) + 1 : 1}`;
      options.push({
        value: nextId,
        label: `${nextId} (new)`,
        isExisting: false,
      });
      return options;
    },
    [laborIdsByCondition],
  );

  const netQtyMap = useMemo(() => {
    const map = {};
    netQuantities.forEach((v, k) => {
      map[k] = v;
    });
    return map;
  }, [netQuantities]);

  const calculateBreakdownUnitPrice = useCallback(
    (item) => {
      const markup = 1 + (formData.defaultMarkupPercentage || 0) / 100;
      const base = item.unitRate > 0 ? item.unitRate : item.unitCost || 0;
      const isCov =
        item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
      const cov = isCov ? parseFloat(item.coveragePerUnit) || 1 : 1;
      return Number((base * cov * markup).toFixed(2));
    },
    [formData.defaultMarkupPercentage],
  );

  const sectionBreakdownTotals = useMemo(() => {
    const totals = {};
    pricingSections.forEach((section) => {
      const breakdownItems = changeItems.filter(
        (i) => i.referenceNumber === section.number && i.includeInBreakdown,
      );
      let materialTotal = 0,
        laborTotal = 0;
      const materialItems = [],
        laborItems = [];
      breakdownItems.forEach((item) => {
        const qty =
          item.breakdownQty ?? Math.abs(item.deltaQty || item.quantity || 0);
        const price =
          item._breakdownUnitPriceManuallySet &&
          item.breakdownUnitPrice !== undefined
            ? item.breakdownUnitPrice
            : calculateBreakdownUnitPrice(item);
        const total = qty * price;
        const bi = {
          ...item,
          breakdownQty: Number(qty.toFixed(2)),
          breakdownUnitPrice: Number(price.toFixed(2)),
          breakdownTotal: Number(total.toFixed(2)),
          breakdownUnit: item.breakdownUnit || item.uom || "EA",
          breakdownDescription:
            item.breakdownDescription ||
            item.customerDescription ||
            item.itemDescription ||
            "",
          isCustom: false,
        };
        if (item.category === "M") {
          materialTotal += total;
          materialItems.push(bi);
        } else {
          laborTotal += total;
          laborItems.push(bi);
        }
      });
      (section.customItems || []).forEach((ci) => {
        const total = (ci.qty || 0) * (ci.unitPrice || 0);
        const cbi = {
          _id: ci._id,
          _localId: `custom-${section.number}-${ci._id}`,
          breakdownQty: ci.qty || 0,
          breakdownUnitPrice: ci.unitPrice || 0,
          breakdownTotal: Number(total.toFixed(2)),
          breakdownUnit: ci.unit || "EA",
          breakdownDescription: ci.description || "",
          category: ci.category || "M",
          isCustom: true,
        };
        if (ci.category === "M") {
          materialTotal += total;
          materialItems.push(cbi);
        } else {
          laborTotal += total;
          laborItems.push(cbi);
        }
      });
      totals[section.number] = {
        materialTotal: Number(materialTotal.toFixed(2)),
        laborTotal: Number(laborTotal.toFixed(2)),
        materialItems,
        laborItems,
      };
    });
    return totals;
  }, [changeItems, pricingSections, calculateBreakdownUnitPrice]);

  const sectionEstimatedCosts = useMemo(() => {
    const costs = {};
    pricingSections.forEach((section) => {
      const items = changeItems.filter(
        (i) => i.referenceNumber === section.number && i.forBudget,
      );
      let mat = 0,
        lab = 0;
      items.forEach((item) => {
        const sq = item.deltaQty || item.quantity || 0;
        const qty = Math.abs(sq);
        const cov = parseFloat(item.coveragePerUnit) || 1;
        const isCov =
          item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
        const cost =
          item.category !== "M" && item.unitRate > 0
            ? item.unitRate
            : item.unitCost || 0;
        const total = isCov ? qty * cov * cost : qty * cost;
        const signed = sq < 0 ? -total : total;
        if (item.category === "M") mat += signed;
        else lab += signed;
      });
      costs[section.number] = {
        material: Number(mat.toFixed(2)),
        labor: Number(lab.toFixed(2)),
        items,
      };
    });
    return costs;
  }, [changeItems, pricingSections]);

  const totals = useMemo(() => {
    let budgetCost = 0;
    if (isTandMBased && tandmCostData) {
      budgetCost = tandmCostData.totals?.totalCost || 0;
      // Before approval, additionally-added items are not yet in the T&M total,
      // so we add them here. After approval they're already merged in — skip them
      // to avoid double-counting.
      if (!wasApproved) {
        changeItems
          .filter((item) => item.forBudget && !item.sourceLineItemId)
          .forEach((item) => {
            const sq = item.deltaQty || item.quantity || 0;
            const qty = Math.abs(sq);
            const cov = parseFloat(item.coveragePerUnit) || 1;
            const isCov =
              item.category === "M" &&
              (isSFUnit(item.uom) || isLFUnit(item.uom));
            const cost =
              item.category !== "M" && item.unitRate > 0
                ? item.unitRate
                : item.unitCost || 0;
            const total = isCov ? qty * cov * cost : qty * cost;
            budgetCost += sq < 0 ? -total : total;
          });
      }
    } else {
      changeItems.forEach((item) => {
        if (!item.forBudget) return;
        const sq = item.deltaQty || item.quantity || 0;
        const qty = Math.abs(sq);
        const cov = parseFloat(item.coveragePerUnit) || 1;
        const isCov =
          item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
        const cost =
          item.category !== "M" && item.unitRate > 0
            ? item.unitRate
            : item.unitCost || 0;
        const total = isCov ? qty * cov * cost : qty * cost;
        budgetCost += sq < 0 ? -total : total;
      });
    }
    let customerSubtotal = 0,
      totalMaterialPrice = 0,
      totalLaborPrice = 0;
    pricingSections.forEach((section) => {
      const bd = sectionBreakdownTotals[section.number] || {
        materialTotal: 0,
        laborTotal: 0,
        materialItems: [],
        laborItems: [],
      };
      const hasBreakdown =
        (bd.materialItems?.length || 0) + (bd.laborItems?.length || 0) > 0;
      const sm = hasBreakdown
        ? bd.materialTotal || 0
        : section.materialPrice || 0;
      const sl = hasBreakdown ? bd.laborTotal || 0 : section.laborPrice || 0;
      totalMaterialPrice += sm;
      totalLaborPrice += sl;
      customerSubtotal += sm + sl;
    });
    const overheadAmount =
      customerSubtotal * ((formData.overheadPercentage || 0) / 100);
    const totalCustomerPrice = customerSubtotal + overheadAmount;
    return {
      budgetCost: Number(budgetCost.toFixed(2)),
      customerSubtotal: Number(customerSubtotal.toFixed(2)),
      totalMaterialPrice: Number(totalMaterialPrice.toFixed(2)),
      totalLaborPrice: Number(totalLaborPrice.toFixed(2)),
      overheadAmount: Number(overheadAmount.toFixed(2)),
      totalCustomerPrice: Number(totalCustomerPrice.toFixed(2)),
      margin: Number((customerSubtotal - Math.abs(budgetCost)).toFixed(2)),
      marginPercent:
        customerSubtotal > 0
          ? Number(
              (
                ((customerSubtotal - Math.abs(budgetCost)) / customerSubtotal) *
                100
              ).toFixed(1),
            )
          : 0,
    };
  }, [
    changeItems,
    pricingSections,
    sectionBreakdownTotals,
    formData.overheadPercentage,
    isTandMBased,
    tandmCostData,
  ]);

  const hasActiveFiltersOrSort = useMemo(() => {
    if (sortConfig.key) return true;
    return Object.entries(changeOrderFilters).some(([k, v]) =>
      k === "description" ? !!v : Array.isArray(v) && v.length > 0,
    );
  }, [sortConfig, changeOrderFilters]);

  // ── Contacts ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!project) return;
    const accountId = project?.accountId?._id || project?.accountId;
    if (!accountId) return;
    const fetchContacts = async () => {
      try {
        setLoadingContacts(true);
        const res = await axios.get(
          `${API_BASE}/contacts/related?relatedAccount=${accountId}&populate=relatedAccount`,
          { headers },
        );
        setContacts(res.data || []);
      } catch (err) {
        console.error("Failed to fetch contacts:", err);
      } finally {
        setLoadingContacts(false);
      }
    };
    fetchContacts();
  }, [project]);

  useEffect(() => {
    if (
      !project ||
      !contacts.length ||
      !savedChangeOrder?.attentionTo?.length
    ) {
      setSelectedAttentionTo([]);
      return;
    }
    const contactOptions = contacts.map((c) => ({
      value: c._id,
      label: `${c.firstName || ""} ${c.lastName || ""}`.trim() || "Unnamed",
      email: c.email || "",
    }));
    const savedIds = savedChangeOrder.attentionTo
      .map((c) => (typeof c === "string" ? c : String(c._id || c.id || "")))
      .filter(Boolean);
    const preselected = contactOptions.filter((o) =>
      savedIds.includes(String(o.value)),
    );
    const ordered = savedChangeOrder.attentionTo
      .map((saved) => {
        const id = String(saved._id || saved);
        return preselected.find((o) => String(o.value) === id);
      })
      .filter(Boolean);
    setSelectedAttentionTo(ordered.length > 0 ? ordered : preselected);
  }, [contacts, savedChangeOrder?.attentionTo, project]);

  useEffect(() => {
    if (savedChangeOrder?.amendments)
      setAmendments(savedChangeOrder.amendments);
  }, [savedChangeOrder?.amendments]);

  // Add this RIGHT AFTER the amendments useEffect:
  const cumulativeCOItems = useMemo(() => {
    const base = [...changeItems];

    const approvedAmendments = amendments.filter(
      (a) => a.status === "Approved",
    );
    approvedAmendments.forEach((amendment) => {
      (amendment.items || []).forEach((amendItem, idx) => {
        if (amendItem.sourceType === "co_item") return;

        base.push({
          ...amendItem,
          _localId: `approved-amend-${amendment._id}-${idx}`,
          _fromApprovedAmendment: amendment.number,
        });
      });
    });

    return base;
  }, [changeItems, amendments]);

  // ── Click outside handler ────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (!showCOFilter) return;
      const ref = coFilterRefs[showCOFilter];
      if (ref?.current && !ref.current.contains(e.target)) {
        if (
          !e.target.closest('[class*="-menu"]') &&
          !e.target.closest('[id^="react-select"]')
        )
          setShowCOFilter(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showCOFilter]);

  // ── Initial data fetch ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [projectRes, vendorsRes, companyRes, coRes] = await Promise.all([
          axios.get(`${API_BASE}/projects/${projectId}`, { headers }),
          axios.get(`${API_BASE}/accounts/vendors`, { headers }),
          axios.get(`${API_BASE}/company-profile`, { headers }),
          axios.get(`${API_BASE}/changeOrders2/${changeOrderId}`, { headers }),
        ]);
        setProject(projectRes.data);
        setVendors(vendorsRes.data);
        setCompanyProfile(companyRes.data || null);
        const co = coRes.data.changeOrder;
        setSavedChangeOrder(co);
        setFormData({
          title: co.title || "",
          customerDescription: co.customerDescription || "",
          scopeOfWork: co.scopeOfWork || "",
          referenceNumber: co.referenceNumber || "",
          termsAndConditions:
            co.termsAndConditions || "Original terms and conditions.",
          overheadPercentage: co.overheadPercentage || 0,
          defaultMarkupPercentage: co.defaultMarkupPercentage || DEFAULT_MARKUP,
          changesToSchedule: co.changesToSchedule || 0,
          approvalStatus: co.approvalStatus || "Pending",
        });
        setSummary({
          description: co.summary?.description || "",
          qtyUnit: co.summary?.qtyUnit || "1",
          billable: co.summary?.billable ?? true,
          revision: co.summary?.revision || 0,
          nonBillableReason: co.summary?.nonBillableReason || "",
        });
        setChangeItems(
          (co.items || []).map((item, i) => {
            const sq = item.deltaQty || item.quantity || 0;
            const qty = Math.abs(sq);
            const cov = parseFloat(item.coveragePerUnit) || 1;
            const isCov =
              item.category === "M" &&
              (isSFUnit(item.uom) || isLFUnit(item.uom));
            const base =
              item.category !== "M" && item.unitRate > 0
                ? item.unitRate
                : item.unitCost || 0;
            const total = isCov ? qty * cov * base : qty * base;
            return {
              ...item,
              _localId: `existing-${i}-${Date.now()}`,
              remainingQty: 0,
              budgetedQty: item.quantity || 0,
              conditionName: item.conditionName || "",
              bidArea: item.bidArea || "",
              amount: sq < 0 ? -total : total,
            };
          }),
        );
        setPricingSections(co.pricingSections || []);
        if (token) {
          try {
            const decoded = jwtDecode(token);
            setUserEmail(decoded.email || "");
            const cc = projectRes.data?.customerContact;
            setEmailForm({
              to: cc?.email || "",
              toContacts: cc
                ? [
                    {
                      value: cc._id,
                      label:
                        `${cc.firstName || ""} ${cc.lastName || ""}`.trim(),
                      email: cc.email || "",
                    },
                  ]
                : [],
              subject: `${projectRes.data?.projectName || "Project"} - Change Order #${co.changeOrderNumber || "Pending"}`,
              body: `Dear ${cc?.firstName || "Client"},\n\nPlease find attached the Change Order for your review.\n\nBest regards,`,
              signature: decoded.displayName || decoded.email || "User",
            });
          } catch (err) {
            console.error("Error decoding token:", err);
          }
        }
      } catch (err) {
        console.error(err);
        showAlert("error", "Error", "Failed to load change order data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [projectId, changeOrderId, token]);

  // ── Net quantities ───────────────────────────────────────────────────────────
  const fetchNetQuantities = useCallback(
    async (force = false) => {
      if (!force && netQuantities.size > 0) return;
      try {
        setNetLoading(true);
        const res = await axios.get(
          `${API_BASE}/net-assemblies/project-by-source/${projectId}`,
          {
            headers,
            params: {
              category: "all",
              includeClosed: "true",
              mode: "combined",
            },
          },
        );
        const {
          originalItems = [],
          changeOrderItems = [],
          combined = [],
        } = res.data;
        const allNetItems =
          combined.length > 0
            ? combined
            : [...originalItems, ...changeOrderItems];
        const newNetMap = new Map();
        const baseItemMap = new Map();
        allNetItems.forEach((ni) => {
          const ownKey = ni.isNewSpec
            ? (ni.lineItemId || ni._id || ni.key)?.toString()
            : (
                ni.key ||
                ni.originalLineItemId ||
                ni.lineItemId ||
                ni._id
              )?.toString();
          if (!ownKey) return;
          const normalized = {
            ...ni,
            _id: ni.lineItemId || ni._id || ownKey,
            itemCode: ni.itemCode || ni.materialCode || ni.laborCode || "",
            itemDescription: ni.itemDescription || ni.costCodeDescription || "",
            requiredQty: ni.netRequiredQty || ni.requiredQty || 0,
            remainingQty: ni.netRemaining || 0,
            budgetedQty: ni.baseRequiredQty || ni.requiredQty || 0,
            unitCost: ni.price || ni.cost || ni.unitRate || 0,
            price: ni.latestPrice || ni.price || 0,
            latestPrice: ni.latestPrice || ni.price || 0,
            coveragePerUnit: ni.coveragePerUnit || 1,
            size: ni.latestSize || ni.size || "",
            uom: ni.uom || "",
            category: ni.category || "M",
            conditionName: ni.conditionName || "",
            bidArea: ni.bidArea || "",
            laborId: ni.laborId || null,
            vendor: ni.vendor?._id || ni.vendor || null,
            isHistorical: !!ni.isHistorical || !!ni.isHistoricalPriceLine,
            isSizeChangeCloseout:
              !!ni.isSizeChangeCloseout || !!ni.isSizeChangeCloseoutRow,
            isNewSpec: !!ni.isNewSpec,
            hasPriceChange: !!ni.hasPriceChange,
            closedOutBySizeChange: !!ni.closedOutBySizeChange,
          };
          newNetMap.set(ownKey, normalized);
          const groupKey = ni.isNewSpec
            ? (ni.lineItemId || ni._id)?.toString()
            : (
                ni.originalLineItemId ||
                ni.key ||
                ni.lineItemId ||
                ni._id
              )?.toString();
          if (!groupKey) return;
          if (ni.closedOutBySizeChange && !ni.isNewSpec) return;
          if (!baseItemMap.has(groupKey))
            baseItemMap.set(groupKey, {
              ...normalized,
              _id: groupKey,
              _netRequiredQty: 0,
              _qtyOrdered: 0,
              _qtyPending: 0,
              _qtyOrderedAtOldPrice: 0,
              _qtyOrderedAtNewPrice: 0,
              _latestPrice: ni.latestPrice || ni.price || 0,
              _hasPriceChange: !!ni.hasPriceChange,
              _seededFromActive: !ni.isHistoricalPriceLine,
            });
          const entry = baseItemMap.get(groupKey);
          if (ni.isHistoricalPriceLine) {
            entry._qtyOrdered += ni.qtyOrdered || 0;
            entry._qtyOrderedAtOldPrice += ni.qtyOrdered || 0;
          } else {
            entry._netRequiredQty += ni.netRequiredQty || 0;
            entry._qtyOrdered += ni.qtyOrdered || 0;
            entry._qtyPending += ni.qtyPending || 0;
            entry._qtyOrderedAtNewPrice += ni.qtyOrdered || 0;
            entry._latestPrice =
              ni.latestPrice || ni.price || entry._latestPrice;
            if (ni.hasPriceChange) entry._hasPriceChange = true;
            if (!entry._seededFromActive) {
              entry.conditionName = ni.conditionName || entry.conditionName;
              entry.bidArea = ni.bidArea || entry.bidArea;
              entry.laborId = ni.laborId || entry.laborId;
              entry.uom = ni.uom || entry.uom;
              entry.size = ni.latestSize || ni.size || entry.size;
              entry.coveragePerUnit =
                ni.latestCoveragePerUnit ||
                ni.coveragePerUnit ||
                entry.coveragePerUnit;
              entry.itemDescription =
                ni.itemDescription ||
                ni.costCodeDescription ||
                entry.itemDescription;
              entry._seededFromActive = true;
            }
          }
        });
        const unifiedBudgetItems = [];
        baseItemMap.forEach((entry) => {
          const netRequiredQty = entry._netRequiredQty;
          const qtyOrdered = entry._qtyOrdered;
          const qtyPending = entry._qtyPending;
          const netRemaining = Math.max(
            0,
            netRequiredQty - entry._qtyOrderedAtNewPrice - qtyPending,
          );
          if (netRequiredQty === 0 && qtyOrdered === 0 && qtyPending === 0)
            return;
          unifiedBudgetItems.push({
            ...entry,
            netRequiredQty,
            qtyOrdered,
            qtyPending,
            netRemaining,
            remainingQty: netRemaining,
            budgetedQty: netRequiredQty,
            unitCost: entry._latestPrice,
            price: entry._latestPrice,
            latestPrice: entry._latestPrice,
            hasPriceChange: entry._hasPriceChange,
          });
        });
        setNetQuantities(newNetMap);
        setBudgetItems(unifiedBudgetItems);
      } catch (err) {
        console.error(err);
        showAlert(
          "error",
          "Error",
          "Could not load current net budget quantities",
        );
      } finally {
        setNetLoading(false);
      }
    },
    [projectId, headers],
  );

  useEffect(() => {
    if (
      (showBudgetModal || showDatabaseModal || showExcelUploadModal) &&
      netQuantities.size === 0
    )
      fetchNetQuantities();
  }, [
    showBudgetModal,
    showDatabaseModal,
    showExcelUploadModal,
    netQuantities.size,
    fetchNetQuantities,
  ]);

  useEffect(() => {
    if (netQuantities.size === 0 || changeItems.length === 0) return;
    setChangeItems((prev) =>
      prev.map((item) => {
        if (!isFromBudget(item)) return item;
        const key = item.sourceLineItemId?.toString();
        const ni = netQuantities.get(key);
        if (!ni) return item;
        return {
          ...item,
          remainingQty: ni.netRemaining ?? item.remainingQty ?? 0,
          budgetedQty: ni.netRequiredQty ?? item.budgetedQty ?? 0,
          availableQty: ni.netRemaining ?? item.remainingQty ?? 0,
          unitCost: ni.latestPrice || item.unitCost || 0,
          price: ni.latestPrice || item.price || 0,
          size: ni.latestSize || item.size || "",
          coveragePerUnit:
            ni.latestCoveragePerUnit || item.coveragePerUnit || "1",
          hasPriceChange: ni.hasPriceChange || item.hasPriceChange || false,
        };
      }),
    );
  }, [netQuantities]);

  // ── Database items ───────────────────────────────────────────────────────────
  const fetchDatabaseItems = useCallback(async () => {
    try {
      setLoadingDatabaseItems(true);
      const res = await axios.get(`${API_BASE}/item-database?limit=5000`, {
        headers,
      });
      setDatabaseItems(res.data.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDatabaseItems(false);
    }
  }, []);

  useEffect(() => {
    if (showDatabaseModal && databaseItems.length === 0) fetchDatabaseItems();
  }, [showDatabaseModal, fetchDatabaseItems, databaseItems.length]);

  // ── Filter helpers ───────────────────────────────────────────────────────────
  const getFilterOptions = useCallback(
    (field) => {
      const unique = new Set();
      budgetItems.forEach((item) => {
        let v;
        if (field === "category") v = item.category;
        if (field === "vendor") v = item.vendor?._id || item.vendor;
        if (field === "bidArea") v = item.bidArea;
        if (field === "condition") {
          const raw = item.conditionName;
          if (raw) {
            const str = String(raw).trim();
            const m = str.match(/^\d+\s*[-–—]\s*(.+)$/);
            v = m ? m[1].trim() : str;
          }
        }
        if (field === "uom") v = item.uom || item.container;
        if (v) unique.add(v);
      });
      if (field === "vendor")
        return Array.from(unique).map((id) => ({
          value: id,
          label: vendors.find((v) => v._id === id)?.companyName || "Unknown",
        }));
      return Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v || "N/A" }));
    },
    [budgetItems, vendors],
  );

  const getDatabaseFilterOptions = useCallback(
    (field) => {
      const unique = new Set();
      databaseItems.forEach((item) => {
        const v = field === "uom" ? item.uom : item[field];
        if (v) unique.add(v);
      });
      return Array.from(unique)
        .sort()
        .map((v) => ({ value: v, label: v }));
    },
    [databaseItems],
  );

  const normalizeConditionName = (cn) => {
    if (!cn) return cn;
    const str = String(cn).trim();
    const m = str.match(/^\d+\s*[-–—]\s*(.+)$/);
    return m ? m[1].trim() : str;
  };

  const getCOFilterOptions = useCallback(
    (field) => {
      const unique = new Set();
      changeItems.forEach((item) => {
        let v;
        switch (field) {
          case "category":
            v = item.category;
            break;
          case "condition":
            v = item.conditionName
              ? normalizeConditionName(item.conditionName)
              : null;
            break;
          case "bidArea":
            v = item.bidArea;
            break;
          case "uom":
            v = item.uom || item.breakdownUnit;
            break;
          case "size":
            v = item.size;
            break;
          case "reference":
            v = item.referenceNumber ? String(item.referenceNumber) : null;
            break;
          case "laborId":
            v = item.laborId;
            break;
          case "itemCode":
            v = item.itemCode || item.itemNumber;
            break;
          default:
            return;
        }
        if (v !== undefined && v !== "") unique.add(v);
      });
      const values = Array.from(unique).sort();
      if (field === "reference")
        return [
          { value: null, label: "— Unassigned —" },
          ...values.map((v) => ({ value: v, label: v })),
        ];
      return values.map((v) => ({ value: v, label: v || "— empty —" }));
    },
    [changeItems],
  );

  const filteredChangeItems = useMemo(
    () =>
      changeItems.filter((item) => {
        if (
          changeOrderFilters.category.length > 0 &&
          !changeOrderFilters.category.includes(item.category)
        )
          return false;
        if (
          changeOrderFilters.condition.length > 0 &&
          !changeOrderFilters.condition.includes(
            normalizeConditionName(item.conditionName),
          )
        )
          return false;
        if (
          changeOrderFilters.bidArea.length > 0 &&
          !changeOrderFilters.bidArea.includes(item.bidArea)
        )
          return false;
        if (
          changeOrderFilters.uom.length > 0 &&
          !changeOrderFilters.uom.includes(item.uom || item.breakdownUnit || "")
        )
          return false;
        if (
          changeOrderFilters.size.length > 0 &&
          !changeOrderFilters.size.includes(item.size)
        )
          return false;
        if (changeOrderFilters.reference.length > 0) {
          const ref = item.referenceNumber
            ? String(item.referenceNumber)
            : null;
          const hasNull = changeOrderFilters.reference.includes(null);
          if (!changeOrderFilters.reference.includes(ref) && !(hasNull && !ref))
            return false;
        }
        if (
          changeOrderFilters.laborId.length > 0 &&
          !changeOrderFilters.laborId.includes(item.laborId)
        )
          return false;
        if (
          changeOrderFilters.itemCode.length > 0 &&
          !changeOrderFilters.itemCode.includes(
            item.itemCode || item.itemNumber || "",
          )
        )
          return false;
        if (changeOrderFilters.description) {
          const s = changeOrderFilters.description.toLowerCase();
          if (
            !(
              item.customerDescription ||
              item.itemDescription ||
              item.breakdownDescription ||
              ""
            )
              .toLowerCase()
              .includes(s)
          )
            return false;
        }
        return true;
      }),
    [changeItems, changeOrderFilters],
  );

  const requestSort = (key) =>
    setSortConfig((c) => {
      if (c.key === key) {
        if (c.direction === "asc") return { key, direction: "desc" };
        if (c.direction === "desc") return { key: null, direction: null };
      }
      return { key, direction: "asc" };
    });
  const getSortIndicator = (key) =>
    sortConfig.key !== key
      ? null
      : sortConfig.direction === "asc"
        ? " ↑"
        : " ↓";

  const sortedAndFilteredItems = useMemo(() => {
    let items = [...filteredChangeItems];
    if (!sortConfig.key || !sortConfig.direction) return items;
    items.sort((a, b) => {
      let av, bv;
      switch (sortConfig.key) {
        case "description":
          av = (a.customerDescription || a.itemDescription || "").toLowerCase();
          bv = (b.customerDescription || b.itemDescription || "").toLowerCase();
          break;
        case "pkgCost": {
          const g = (i) => {
            const c =
              i.category === "M" && (isSFUnit(i.uom) || isLFUnit(i.uom))
                ? parseFloat(i.coveragePerUnit) || 1
                : 1;
            return (i.unitCost || 0) * c;
          };
          av = g(a);
          bv = g(b);
          break;
        }
        case "deltaQty":
          av = Number(a.deltaQty ?? a.quantity ?? 0);
          bv = Number(b.deltaQty ?? b.quantity ?? 0);
          break;
        case "unitCost":
          av = Number(a.unitCost ?? 0);
          bv = Number(b.unitCost ?? 0);
          break;
        case "amount":
          av = Number(a.amount ?? 0);
          bv = Number(b.amount ?? 0);
          break;
        case "referenceNumber":
          av = a.referenceNumber != null ? Number(a.referenceNumber) : -999999;
          bv = b.referenceNumber != null ? Number(b.referenceNumber) : -999999;
          break;
        default:
          av = (a[sortConfig.key] || "").toString().toLowerCase();
          bv = (b[sortConfig.key] || "").toString().toLowerCase();
      }
      if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
      if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return items;
  }, [filteredChangeItems, sortConfig]);

  // ── Item handlers ────────────────────────────────────────────────────────────
  const handleMoveItem = useCallback((localId, direction) => {
    setChangeItems((prev) => {
      const items = prev.slice();
      const idx = items.findIndex((i) => i._localId === localId);
      if (idx < 0) return prev;
      const target = direction === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= items.length) return prev;
      const [removed] = items.splice(idx, 1);
      items.splice(target, 0, removed);
      return items;
    });
  }, []);

  const handleUpdateItem = (localId, field, value) => {
    setChangeItems((prev) =>
      prev.map((item) => {
        if (item._localId !== localId) return item;
        const updated = { ...item };
        if (field === "quantity" || field === "deltaQty") {
          const raw = String(value).trim();
          if (raw === "" || raw === "-" || raw === "-." || raw === ".") {
            updated._rawDeltaInput = raw;
            updated.deltaQty = 0;
            updated.quantity = 0;
            updated.amount = 0;
            if (!updated._breakdownQtyManuallySet) updated.breakdownQty = 0;
            return updated;
          }
          const parsed = parseFloat(raw);
          if (isNaN(parsed)) return item;
          delete updated._rawDeltaInput;
          let nd = item.category === "L" ? parsed : Math.round(parsed);
          if (isFromBudget(item)) {
            const aq =
              item.availableQty ?? item.remainingQty ?? item.budgetedQty ?? 0;
            if (nd < -Math.max(0, aq)) nd = -Math.max(0, aq);
          } else {
            if (nd < 0) nd = 0;
          }
          updated.deltaQty = nd;
          updated.quantity = Math.abs(nd);
          if (!updated._breakdownQtyManuallySet)
            updated.breakdownQty = Math.abs(nd);
        } else if (field === "breakdownQty") {
          updated.breakdownQty = parseFloat(value) || 0;
          updated._breakdownQtyManuallySet = true;
        } else if (field === "breakdownUnitPrice") {
          updated.breakdownUnitPrice = parseFloat(value) || 0;
        } else if (field === "breakdownDescription") {
          updated.breakdownDescription = value;
        } else if (field === "includeInBreakdown") {
          updated.includeInBreakdown = value;
          if (value && updated.breakdownQty === undefined) {
            updated.breakdownQty = Math.abs(
              updated.deltaQty || updated.quantity || 0,
            );
            updated.breakdownUnitPrice = updated.unitCost || 0;
            updated.breakdownDescription =
              updated.customerDescription || updated.itemDescription || "";
          }
        } else if (field === "conditionName") {
          updated.conditionName = value;
          updated.laborId = null;
        } else {
          updated[field] = value;
          if (field === "size") {
            const nc = parseSizeToCoverage(value, updated.uom);
            if (nc !== null) updated.coveragePerUnit = String(nc);
          }
        }
        const qty = Math.abs(updated.deltaQty || 1);
        const cov = parseFloat(updated.coveragePerUnit) || 1;
        const isCov =
          updated.category === "M" &&
          (isSFUnit(updated.uom) || isLFUnit(updated.uom));
        const cost =
          updated.category !== "M" && updated.unitRate > 0
            ? updated.unitRate
            : updated.unitCost || 0;
        const total = isCov ? qty * cov * cost : qty * cost;
        updated.amount = (updated.deltaQty || 0) < 0 ? -total : total;
        return updated;
      }),
    );
  };

  const handleRemoveItem = (localId) =>
    setChangeItems((prev) => prev.filter((i) => i._localId !== localId));

  // ── Budget item add ───────────────────────────────────────────────────────────
  const addBudgetItemsToChangeOrder = (targetSectionNumber = null) => {
    if (selectedBudgetItems.length === 0) {
      showAlert("error", "Error", "Please select at least one item");
      return;
    }
    const isDupe = (ni, existing) =>
      existing.some((e) => {
        const sameCode =
          (ni.itemCode || ni._id) === (e.itemCode || e.itemNumber);
        if (!sameCode) return false;
        if (ni.sourceLineItemId && e.sourceLineItemId)
          return (
            ni.sourceLineItemId.toString() === e.sourceLineItemId.toString()
          );
        if (ni.sourceLineItemId && e.itemDatabaseId) return false;
        if (ni.itemDatabaseId && e.sourceLineItemId) return false;
        if (ni.itemDatabaseId && e.itemDatabaseId)
          return (
            ni.itemDatabaseId === e.itemDatabaseId &&
            (ni.conditionName || "") === (e.conditionName || "") &&
            (ni.bidArea || "") === (e.bidArea || "") &&
            (ni.size || "") === (e.size || "")
          );
        return (
          sameCode &&
          (ni.conditionName || "") === (e.conditionName || "") &&
          (ni.bidArea || "") === (e.bidArea || "") &&
          (ni.size || "") === (e.size || "")
        );
      });
    const dupes = selectedBudgetItems.filter((i) => isDupe(i, changeItems));
    if (dupes.length > 0)
      showAlert(
        "warning",
        "Duplicates Skipped",
        `${dupes.length} duplicate(s) skipped: ${dupes.map((d) => `${d.itemCode} (${d.conditionName || "no assembly"})`).join(", ")}.`,
      );
    const newItems = selectedBudgetItems
      .filter((i) => !isDupe(i, changeItems))
      .map((item, idx) => {
        const itemKey = (
          item.originalLineItemId ||
          item.lineItemId ||
          item._id ||
          item.key
        )?.toString();
        const ni = netQuantities.get(itemKey) || item;
        const remaining = ni.netRemaining ?? item.remainingQty ?? 0;
        const budgeted =
          ni.netRequiredQty ??
          item.budgetedQty ??
          item.requiredQty ??
          remaining;
        let rawDelta = item.deltaQty || 1;
        const qty = Math.abs(rawDelta);
        const isMat = item.category === "M";
        const baseCost =
          ni.latestPrice ??
          (isMat
            ? item.cost || item.price
            : item.unitRate || item.cost || item.price) ??
          0;
        const unitCost = Number(baseCost.toFixed(2));
        let cov =
          ni.latestCoveragePerUnit ?? parseFloat(item.coveragePerUnit) ?? 1;
        let size = ni.latestSize || item.size || "";
        if (isMat && cov === 1 && (isSFUnit(item.uom) || isLFUnit(item.uom)))
          size = isSFUnit(item.uom) ? "1' x 1'" : "1'";
        const baseAmt = isMat ? qty * cov * unitCost : qty * unitCost;
        return {
          _localId: `budget-${Date.now()}-${idx}`,
          itemNumber: String(changeItems.length + 1 + idx),
          sourceAssemblyId: item.assemblyId,
          sourceLineItemId: itemKey,
          isFromBudget: true,
          laborId: item.laborId,
          itemCode:
            item.itemCode || item.materialCode || item.laborCode || item._id,
          type: isMat ? "material" : "labor",
          category: item.category,
          sectionNumber: item.sectionNumber,
          title: item.itemDescription || item.costCodeDescription || "",
          itemDescription:
            item.itemDescription || item.costCodeDescription || "",
          customerDescription:
            item.itemDescription || item.costCodeDescription || "",
          conditionName: item.conditionName || "",
          bidArea: item.bidArea || "",
          deltaQty: rawDelta,
          quantity: qty,
          unitCost,
          unitRate: isMat ? 0 : baseCost,
          amount: rawDelta < 0 ? -baseAmt : baseAmt,
          size,
          uom: item.uom || "",
          coveragePerUnit: String(cov),
          vendor: item.vendor?._id || item.vendor || null,
          remainingQty: remaining,
          budgetedQty: budgeted,
          availableQty: ni.netRemaining ?? item.netRemaining ?? remaining,
          forBudget: true,
          referenceNumber: targetSectionNumber ?? null,
          hasPriceChange: ni.hasPriceChange || item.hasPriceChange || false,
          originalPrice: ni.originalPrice ?? unitCost,
        };
      });
    setChangeItems((prev) => [...prev, ...newItems]);
    setSelectedBudgetItems([]);
    setShowBudgetModal(false);
    setAddingToSection(null);
    if (newItems.length > 0)
      showAlert(
        "success",
        "Success",
        `Added ${newItems.length} item(s)${targetSectionNumber ? ` to Section ${targetSectionNumber}` : ""}`,
      );
  };

  // ── Database item add ─────────────────────────────────────────────────────────
  const addDatabaseItemsToChangeOrder = (targetSectionNumber = null) => {
    if (selectedDatabaseItems.length === 0) {
      showAlert("error", "Error", "Please select at least one item");
      return;
    }
    const isDupe = (ni, existing) =>
      existing.some((e) => {
        const sc = ni.itemCode === (e.itemCode || e.itemNumber);
        if (!sc) return false;
        if (e.sourceLineItemId) return false;
        if (ni.itemDatabaseId && e.itemDatabaseId)
          return (
            ni.itemDatabaseId === e.itemDatabaseId &&
            (ni.conditionName || "") === (e.conditionName || "") &&
            (ni.bidArea || "") === (e.bidArea || "") &&
            (ni.size || "") === (e.size || "")
          );
        return (
          sc &&
          (ni.conditionName || "") === (e.conditionName || "") &&
          (ni.bidArea || "") === (e.bidArea || "") &&
          (ni.size || "") === (e.size || "")
        );
      });
    const dupes = selectedDatabaseItems.filter((sel) => {
      const item = databaseItems.find((d) => d.itemCode === sel.itemCode);
      if (!item) return false;
      return isDupe(
        {
          itemCode: item.itemCode,
          itemDatabaseId: item._id,
          conditionName: sel.conditionName || "",
          bidArea: sel.bidArea || "",
          size: sel.editedSize || item.size || "",
        },
        changeItems,
      );
    });
    if (dupes.length > 0) {
      showAlert(
        "error",
        "Duplicate Items",
        `Already added: ${dupes.map((d) => `${d.itemCode} (${d.conditionName || "no assembly"})`).join(", ")}.`,
      );
      return;
    }
    const nextNum = changeItems.length + 1;
    const newItems = selectedDatabaseItems
      .map((sel, idx) => {
        const item = databaseItems.find((d) => d.itemCode === sel.itemCode);
        if (!item) return null;
        let unitCost = item.defaultCost || 0;
        const vtu =
          sel.selectedVendor ||
          item.defaultVendor?._id ||
          item.defaultVendor ||
          null;
        if (vtu && Array.isArray(item.vendorPrices)) {
          const vp = item.vendorPrices.find(
            (p) => (p.vendor?._id || p.vendor) === vtu,
          );
          if (vp) unitCost = vp.price || 0;
        }
        const qty = Math.abs(sel.deltaQty || 1);
        const uom = item.uom || "";
        const isCov = item.category === "M" && (isSFUnit(uom) || isLFUnit(uom));
        let size = sel.editedSize?.trim() || item.size?.trim() || "";
        let cov =
          sel.editedCoverage && sel.editedCoverage !== 1
            ? sel.editedCoverage
            : isCov && size
              ? (parseSizeToCoverage(size, uom) ??
                parseFloat(item.coveragePerUnit) ??
                1)
              : parseFloat(item.coveragePerUnit) || 1;
        if (!size && isCov && cov === 1)
          size = isSFUnit(uom) ? "1' x 1'" : "1'";
        const amount = isCov
          ? Number((qty * cov * unitCost).toFixed(2))
          : Number((qty * unitCost).toFixed(2));
        return {
          _localId: `database-${Date.now()}-${idx}`,
          itemNumber: String(nextNum + idx),
          itemDatabaseId: item._id,
          isFromDatabase: true,
          itemCode: sel.itemCode,
          type: item.category === "M" ? "material" : "labor",
          category: item.category,
          sectionNumber: item.sectionNumber || "",
          title: item.description || "",
          itemDescription: item.description || "",
          customerDescription: item.description || "",
          conditionName: sel.conditionName || "",
          bidArea: sel.bidArea || "",
          deltaQty: qty,
          quantity: qty,
          unitCost,
          amount,
          size,
          uom,
          coveragePerUnit: String(cov),
          vendor: vtu,
          forBudget: true,
          referenceNumber: targetSectionNumber ?? null,
        };
      })
      .filter(Boolean);
    setChangeItems((prev) => [...prev, ...newItems]);
    setSelectedDatabaseItems([]);
    setShowDatabaseModal(false);
    setDatabaseFilters({ category: [], uom: [], search: "" });
    setAddingToSection(null);
    showAlert(
      "success",
      "Success",
      `Added ${newItems.length} item(s)${targetSectionNumber ? ` to Section ${targetSectionNumber}` : ""}`,
    );
  };

  // ── Excel item add ────────────────────────────────────────────────────────────
  const addExcelUploadedItems = (items, targetSectionNumber = null) => {
    if (items.length === 0) {
      showAlert("error", "Error", "No items to add");
      return;
    }
    const getKey = (i) =>
      `${i.itemCode || i._id}-${i.conditionName || ""}-${i.bidArea || ""}`;
    const existing = new Set(changeItems.map(getKey));
    const dupes = items.filter((i) => existing.has(getKey(i)));
    if (dupes.length > 0)
      showAlert(
        "warning",
        "Some Duplicates Skipped",
        `${dupes.length} duplicate(s) skipped: ${dupes.map((d) => d.itemCode).join(", ")}`,
      );
    const newItems = items.filter((i) => !existing.has(getKey(i)));
    if (newItems.length === 0) {
      showAlert("error", "Error", "All items are duplicates");
      return;
    }
    const nextNum = changeItems.length + 1;
    const processed = newItems.map((item, idx) => {
      const uom = item.uom || "";
      const isCov = item.category === "M" && (isSFUnit(uom) || isLFUnit(uom));
      const size = item.size || "";
      let cov = parseFloat(item.coveragePerUnit) || 1;
      if (isCov && size) {
        const p = parseSizeToCoverage(size, uom);
        if (p !== null) cov = p;
      }
      const qty = Math.abs(item.deltaQty || item.quantity || 0);
      const uc = item.unitCost || 0;
      const base = isCov ? qty * cov * uc : qty * uc;
      const sq = item.deltaQty || item.quantity || 0;
      return {
        ...item,
        _localId: item._localId || `excel-${Date.now()}-${idx}`,
        itemNumber: String(nextNum + idx),
        coveragePerUnit: String(cov),
        referenceNumber: targetSectionNumber ?? item.referenceNumber ?? null,
        amount: sq < 0 ? -base : base,
      };
    });
    setChangeItems((prev) => [...prev, ...processed]);
    setAddingToSection(null);
    showAlert(
      "success",
      "Success",
      `Added ${processed.length} item(s)${targetSectionNumber ? ` to Section ${targetSectionNumber}` : ""} from Excel`,
    );
  };

  // ── Pricing section handlers ──────────────────────────────────────────────────
  const addPricingSection = () => {
    const n = pricingSections.length + 1;
    setPricingSections((prev) => [
      ...prev,
      { number: n, title: "", materialPrice: 0, laborPrice: 0 },
    ]);
    setExpandedSections((prev) => new Set([...prev, n]));
  };
  const updatePricingSection = (index, field, value) =>
    setPricingSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
  const removePricingSection = (index) => {
    const rn = pricingSections[index].number;
    setChangeItems((prev) =>
      prev.map((i) =>
        i.referenceNumber === rn ? { ...i, referenceNumber: null } : i,
      ),
    );
    setPricingSections((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, number: i + 1 })),
    );
    setChangeItems((prev) =>
      prev.map((i) =>
        i.referenceNumber > rn
          ? { ...i, referenceNumber: i.referenceNumber - 1 }
          : i,
      ),
    );
  };
  const toggleSectionExpand = (index) => {
    const n = pricingSections[index].number;
    setExpandedSections((prev) => {
      const s = new Set(prev);
      s.has(n) ? s.delete(n) : s.add(n);
      return s;
    });
  };
  const addCustomPricingItem = (sectionIndex, category) =>
    setPricingSections((prev) =>
      prev.map((s, i) =>
        i !== sectionIndex
          ? s
          : {
              ...s,
              customItems: [
                ...(s.customItems || []),
                {
                  _id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  category,
                  description: "",
                  qty: 1,
                  unit: "EA",
                  unitPrice: 0,
                  total: 0,
                },
              ],
            },
      ),
    );
  const updateCustomPricingItem = (sectionIndex, itemId, field, value) =>
    setPricingSections((prev) =>
      prev.map((s, i) => {
        if (i !== sectionIndex) return s;
        return {
          ...s,
          customItems: (s.customItems || []).map((item) => {
            if (item._id !== itemId) return item;
            const updated = { ...item, [field]: value };
            updated.total = (updated.qty || 0) * (updated.unitPrice || 0);
            return updated;
          }),
        };
      }),
    );
  const removeCustomPricingItem = (sectionIndex, itemId) =>
    setPricingSections((prev) =>
      prev.map((s, i) =>
        i !== sectionIndex
          ? s
          : {
              ...s,
              customItems: (s.customItems || []).filter(
                (item) => item._id !== itemId,
              ),
            },
      ),
    );

  const handleSectionAdd = (sectionNumber, type) => {
    setAddingToSection(sectionNumber);
    setExpandedSections((prev) => new Set([...prev, sectionNumber]));
    setSectionActiveTab((prev) => ({ ...prev, [sectionNumber]: "items" }));
    if (type === "budget") setShowBudgetModal(true);
    else if (type === "database") setShowDatabaseModal(true);
    else if (type === "excel") setShowExcelUploadModal(true);
  };

  const hasBreakdownItems = (sn, category = null) => {
    const hasBudget = changeItems.some(
      (i) =>
        i.referenceNumber === sn &&
        i.includeInBreakdown &&
        (category ? i.category === category : true),
    );
    const section = pricingSections.find((s) => s.number === sn);
    const hasCustom = (section?.customItems || []).some((i) =>
      category ? i.category === category : true,
    );
    return hasBudget || hasCustom;
  };

  const getSectionTotal = (sn) => {
    if (hasBreakdownItems(sn))
      return (
        (sectionBreakdownTotals[sn]?.materialTotal || 0) +
        (sectionBreakdownTotals[sn]?.laborTotal || 0)
      );
    const section = pricingSections.find((s) => s.number === sn);
    return (section?.materialPrice || 0) + (section?.laborPrice || 0);
  };

  // ── Form change ───────────────────────────────────────────────────────────────
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: [
        "overheadPercentage",
        "defaultMarkupPercentage",
        "changesToSchedule",
      ].includes(name)
        ? parseFloat(value) || 0
        : value,
    }));
  };

  const validateAllItemsHaveRequiredFields = () => {
    const itemsToValidate = isTandMBased
      ? changeItems.filter((i) => !i.sourceLineItemId)
      : changeItems;
    const missing = itemsToValidate.filter(
      (i) => !i.bidArea?.trim() || !i.conditionName?.trim(),
    );
    if (missing.length === 0) return true;
    showAlert(
      "error",
      "Missing Required Fields",
      `The following ${missing.length} item(s) are missing Bid Area or Assembly:\n\n${missing.map((i, idx) => `${idx + 1}) ${i.itemCode || "—"} (${i.customerDescription?.slice(0, 40) || "no desc"}...)`).join("\n")}\n\nPlease fill in both fields for every item before saving.`,
    );
    return false;
  };

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSave = async (e) => {
    e?.preventDefault();
    if (!validateAllItemsHaveRequiredFields()) return;
    if (!formData.title.trim()) {
      showAlert("error", "Error", "Title is required");
      return;
    }

    // For T&M COs, items are optional — the T&M budget is the cost base.
    // But if the PM added extra items we should still save them.
    // Only block if it's a non-T&M CO with no items.
    if (!isTandMBased && changeItems.length === 0 && !wasApproved) {
      showAlert("error", "Error", "At least one item is required");
      return;
    }

    try {
      setIsSaving(true);
      let payload = {};

      if (wasApproved) {
        payload = {
          pricingSections,
          attentionTo: selectedAttentionTo.map((c) => c.value),
          overheadPercentage: formData.overheadPercentage,
          referenceNumber: formData.referenceNumber,
          summary: {
            billable: summary.billable,
            description: summary.description,
            qtyUnit: summary.qtyUnit,
            revision: summary.revision,
            nonBillableReason: summary.billable
              ? ""
              : summary.nonBillableReason || "",
          },
          // Always send items (even for T&M) so referenceNumber updates persist
          items: changeItems
            .filter((i) => i._id)
            .map((i) => ({
              _id: i._id,
              referenceNumber: i.referenceNumber ?? null,
            })),
        };
      } else {
        // Pending / Rejected — full update. Send ALL items regardless of T&M.
        payload = {
          title: formData.title,
          project: projectId,
          items: changeItems.map(({ _localId, ...i }) => i), // ← no isTandMBased guard
          pricingSections,
          attentionTo: selectedAttentionTo.map((c) => c.value),
          referenceNumber: formData.referenceNumber,
          overheadPercentage: formData.overheadPercentage,
          defaultMarkupPercentage: formData.defaultMarkupPercentage,
          changesToSchedule: formData.changesToSchedule,
          termsAndConditions: formData.termsAndConditions,
          customerDescription: formData.customerDescription,
          scopeOfWork: formData.scopeOfWork,
          summary: {
            ...summary,
            nonBillableReason: summary.billable
              ? ""
              : summary.nonBillableReason || "",
          },
        };
      }

      await axios.put(`${API_BASE}/changeOrders2/${changeOrderId}`, payload, {
        headers,
      });
      showAlert(
        "success",
        "Success",
        wasApproved
          ? "Pricing updated successfully"
          : "Change order updated successfully",
      );
    } catch (err) {
      console.error("Save error:", err);
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to save changes",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Approve / Reject ──────────────────────────────────────────────────────────
  const handleApprove = async () => {
    if (isApproving) return;

    // For T&M COs, skip the bidArea/conditionName validation on T&M-sourced
    // items (they have no editable fields) but still validate any new items
    // the PM added directly.
    if (!isTandMBased) {
      const missing = changeItems.filter(
        (i) => !i.bidArea?.trim() || !i.conditionName?.trim(),
      );
      if (missing.length > 0) {
        setShowApproveModal(false);
        showAlert(
          "error",
          "Missing Required Fields",
          `${missing.length} item(s) are missing Bid Area or Assembly. Fill in both before approving.`,
        );
        return;
      }
    } else {
      // T&M COs: only validate items PM added directly (no sourceLineItemId).
      // T&M-sourced items inherit metadata from the T&M budget.
      const newItems = changeItems.filter((i) => !i.sourceLineItemId);
      const missingOnNew = newItems.filter(
        (i) => !i.bidArea?.trim() || !i.conditionName?.trim(),
      );
      if (missingOnNew.length > 0) {
        setShowApproveModal(false);
        showAlert(
          "error",
          "Missing Required Fields",
          `${missingOnNew.length} newly-added item(s) are missing Bid Area or Assembly:\n\n` +
            missingOnNew
              .map(
                (i, idx) =>
                  `${idx + 1}) ${i.itemCode || "—"} — ${i.customerDescription?.slice(0, 50) || "no description"}`,
              )
              .join("\n") +
            "\n\nFill in Bid Area and Assembly for each before approving.",
        );
        return;
      }
    }

    setIsApproving(true);
    setIsSaving(true);

    try {
      // Save first with ALL items (no isTandMBased guard)
      const items = changeItems.map(({ _localId, ...i }) => i);
      await axios.put(
        `${API_BASE}/changeOrders2/${changeOrderId}`,
        {
          title: formData.title,
          project: projectId,
          items, // ← full items array, T&M approve route handles routing them
          pricingSections,
          overheadPercentage: formData.overheadPercentage,
          defaultMarkupPercentage: formData.defaultMarkupPercentage,
          changesToSchedule: formData.changesToSchedule,
          termsAndConditions: formData.termsAndConditions,
          customerDescription: formData.customerDescription,
          scopeOfWork: formData.scopeOfWork,
          summary,
        },
        { headers },
      );

      const res = await axios.put(
        `${API_BASE}/changeOrders2/${changeOrderId}/approve`,
        {},
        { headers },
      );

      setSavedChangeOrder(res.data.changeOrder);
      setFormData((prev) => ({ ...prev, approvalStatus: "Approved" }));
      setShowApproveModal(false);
      showAlert(
        "success",
        "Approved!",
        `Change Order ${res.data.changeOrder.changeOrderNumber} has been approved.`,
      );
    } catch (err) {
      console.error(err);
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to approve",
      );
    } finally {
      setIsSaving(false);
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsSaving(true);
      const res = await axios.put(
        `${API_BASE}/changeOrders2/${changeOrderId}/reject`,
        {},
        { headers },
      );
      setSavedChangeOrder(res.data.changeOrder);
      setFormData((prev) => ({ ...prev, approvalStatus: "Rejected" }));
      setShowRejectModal(false);
      showAlert(
        "success",
        "Rejected",
        `Change Order ${res.data.changeOrder.changeOrderNumber} has been rejected.`,
      );
    } catch (err) {
      console.error(err);
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to reject",
      );
    } finally {
      setIsSaving(false);
      setIsApproving(false);
    }
  };

  const handleUnlinkTandM = async () => {
    try {
      setIsSaving(true);
      await axios.put(
        `${API_BASE}/changeOrders2/${changeOrderId}/unlink-tandm`,
        {},
        { headers },
      );
      setShowUnlinkConfirm(false);
      window.location.reload();
    } catch (err) {
      showAlert(
        "error",
        "Error",
        err.response?.data?.message || "Failed to unlink T&M",
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Client-side PDF merge ─────────────────────────────────────────────────────
  const mergePDFBlobs = async (...blobs) => {
    const merged = await PDFDocument.create();
    for (const blob of blobs) {
      if (!blob) continue;
      const arrayBuffer = await blob.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      const pages = await merged.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((page) => merged.addPage(page));
    }
    const mergedBytes = await merged.save();
    return new Blob([mergedBytes], { type: "application/pdf" });
  };
  // ── T&M combined PDF blob (mirrors TandMView2.generateCombinedPDF) ───────────
  // ── T&M per-entry PDFs merged in order ────────────────────────────────────────
  const generateTandMPDFBlob = async () => {
    if (!isTandMBased || !tandmId || !companyProfile) return null;
    try {
      const res = await axios.get(
        `${API_BASE}/time-and-material2/${projectId}/${tandmId}`,
        { headers },
      );
      const tandm = res.data;
      if (!tandm) return null;

      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

      const b64parse = (v) => {
        if (typeof v !== "string" || !base64Regex.test(v)) return null;
        const s = v.replace(base64Regex, "");
        if (!validBase64.test(s)) throw new Error("Invalid base64");
        const bin = window.atob(s);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        return bytes.buffer;
      };

      const fetchB64 = async (url) => {
        try {
          const r = await fetch(url, { mode: "cors" });
          if (!r.ok) return null;
          const blob = await r.blob();
          return new Promise((resolve) => {
            const rd = new FileReader();
            rd.onloadend = () => resolve(rd.result);
            rd.readAsDataURL(blob);
          });
        } catch {
          return null;
        }
      };

      const sanitize = (str) => {
        if (!str) return "";
        return str
          .replace(/[\u2028\u2029]/g, " ")
          .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "")
          .replace(/[\uFFF9-\uFFFF]/g, "")
          .replace(/[^\t\n\r\x20-\uD7FF\uE000-\uFFFD]/gu, "")
          .trim();
      };

      const isCov = (uom) => isSFUnit(uom) || isLFUnit(uom);
      const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
      const logoB64 = companyProfile?.logo
        ? await fetchB64(companyProfile.logo)
        : null;

      // Build ordered list of entries: Entry 1 (original) first,
      // then additional entries sorted by createdAt ascending
      const additionalEntries = [...(tandm.entries || [])].sort(
        (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      );

      const allEntries = [
        {
          label: "Entry 1",
          items: tandm.items || [],
          scopeOfWork: tandm.scopeOfWork,
          signedBy: tandm.signedBy,
          signatureUrl: tandm.signature,
          images: tandm.images || [],
          status: tandm.status,
          date: tandm.signedAt || tandm.createdAt,
        },
        ...additionalEntries.map((e) => ({
          label: e.name,
          items: e.items || [],
          scopeOfWork: e.scopeOfWork,
          signedBy: e.signedBy,
          signatureUrl: e.signature,
          images: e.images || [],
          status: e.status,
          date: e.signedAt || e.createdAt,
        })),
      ].filter(
        (e) =>
          e.status !== "Rejected" &&
          e.items.some((i) => i.forCustomer !== false),
      );

      if (allEntries.length === 0) return null;

      // Generate one PDF blob per entry (exactly like TandMEntriesPanel.generateEntryPDF)
      const generateSingleEntryBlob = async (entry) => {
        const pdfItems = (entry.items || [])
          .filter((i) => i.forCustomer !== false)
          .map((item, idx) => {
            const uom = item.uom || "EA";
            const coverage = isCov(uom)
              ? parseFloat(item.coveragePerUnit) || 1
              : 1;
            const amount = Math.abs(item.amount || item.totalCost || 0);
            return {
              itemNo: idx + 1,
              description:
                item.customerDescription ||
                item.title ||
                item.itemDescription ||
                "Unnamed",
              unit: isCov(uom) ? "PC" : uom,
              quantity: item.quantity || item.deltaQty || 0,
              price: ((item.unitCost || 0) * coverage).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              }),
              amount: amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              }),
            };
          });

        if (pdfItems.length === 0) return null;

        const subtotal = pdfItems.reduce(
          (s, i) => s + (parseFloat(i.amount.replace(/[^0-9.-]+/g, "")) || 0),
          0,
        );
        const taxAmt = subtotal * (taxRate / 100);
        const sigB64 = entry.signatureUrl
          ? await fetchB64(entry.signatureUrl)
          : null;
        const imgs = [];
        for (const url of entry.images || []) {
          const b64 = await fetchB64(url);
          if (b64) imgs.push({ image: b64 });
        }

        const templateRes = await fetch("/t_and_m_template.docx");
        if (!templateRes.ok) throw new Error("Failed to load T&M template");
        const zip = new PizZip(await templateRes.arrayBuffer());
        const doc = new Docxtemplater(zip, {
          paragraphLoop: true,
          linebreaks: true,
          modules: [
            new ImageModule({
              getImage: b64parse,
              getSize: (img, val, name) =>
                name === "companyLogo"
                  ? [150, 100]
                  : name === "signature"
                    ? [100, 150]
                    : [200, 200],
            }),
          ],
        });

        doc.setData({
          projectName: project?.projectName || "Unknown Project",
          date: entry.date
            ? new Date(entry.date).toLocaleDateString()
            : new Date().toLocaleDateString(),
          preparedBy: tandm.createdBy?.displayName || "Unknown",
          timeAndMaterialNumber: `${tandm.timeAndMaterialNumber || "Draft"} – ${entry.label}`,
          scopeOfWork: sanitize(entry.scopeOfWork || "N/A"),
          signedBy: entry.signedBy || "N/A",
          companyName: companyProfile?.companyName || "",
          companyAddress: companyProfile?.address
            ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
            : "N/A",
          companyLogo: logoB64 || null,
          signature: sigB64 || null,
          items: pdfItems,
          pageBreak: "\f",
          hasImages: imgs.length > 0,
          images: imgs,
          subtotalPrice: subtotal.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          taxRate: taxRate.toFixed(2),
          taxAmount: taxAmt.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          totalPrice: (subtotal + taxAmt).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          overheadPercentage: (tandm.overheadPercentage || 0).toFixed(2),
        });
        doc.render();

        const docxBlob = doc.getZip().generate({
          type: "blob",
          mimeType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });

        const fd = new FormData();
        fd.append("docx", docxBlob, `TandM_${tandmId}_${entry.label}.docx`);
        fd.append("documentType", "TandM");
        fd.append("identifier", `${tandmId}_${entry.label}`);

        const pdfRes = await axios.post(
          `${API_BASE}/upload/convert-to-pdf`,
          fd,
          {
            headers: { ...headers, "Content-Type": "multipart/form-data" },
            responseType: "blob",
          },
        );
        if (pdfRes.status !== 200)
          throw new Error("T&M entry PDF conversion failed");

        const entryPdfBlob = new Blob([pdfRes.data], {
          type: "application/pdf",
        });

        // If no images, remove the last page (the blank images page) using pdf-lib
        if (imgs.length === 0) {
          const arrayBuffer = await entryPdfBlob.arrayBuffer();
          const pdfDoc = await PDFDocument.load(arrayBuffer);
          const pageCount = pdfDoc.getPageCount();
          if (pageCount > 1) {
            pdfDoc.removePage(pageCount - 1);
          }
          const trimmedBytes = await pdfDoc.save();
          return new Blob([trimmedBytes], { type: "application/pdf" });
        }

        return entryPdfBlob;
      };

      // Generate all entry PDFs in order (sequential to preserve order)
      const entryBlobs = [];
      for (const entry of allEntries) {
        const blob = await generateSingleEntryBlob(entry);
        if (blob) entryBlobs.push(blob);
      }

      if (entryBlobs.length === 0) return null;

      // Merge all entry PDFs into one using pdf-lib
      return await mergePDFBlobs(...entryBlobs);
    } catch (err) {
      console.error("generateTandMPDFBlob error:", err);
      return null;
    }
  };
  // ── PDF generation ────────────────────────────────────────────────────────────
  const generateChangeOrderPDF = async (forEmail = false) => {
    if (!companyProfile) {
      showAlert(
        "error",
        "Error",
        "Cannot generate PDF: Company profile is not set.",
      );
      return null;
    }
    setIsGeneratingPDF(true);
    try {
      const response = await fetch("/change_order_template2.docx");
      if (!response.ok)
        throw new Error(`Failed to load template: ${response.statusText}`);
      const templateArrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(templateArrayBuffer);
      let imageBase64 = null;
      if (companyProfile?.logo) {
        const lr = await fetch(companyProfile.logo);
        if (lr.ok) {
          const lb = await lr.blob();
          imageBase64 = await new Promise((resolve) => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result);
            r.readAsDataURL(lb);
          });
        }
      }
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [
          new ImageModule({
            getImage: (v) => {
              if (typeof v !== "string") return null;
              const b64 = v.replace(/^data:image\/\w+;base64,/, "");
              return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)).buffer;
            },
            getSize: () => [125, 90],
          }),
        ],
      });
      const pricingSectionsData = pricingSections.map((section) => {
        const bd = sectionBreakdownTotals[section.number] || {
          materialItems: [],
          laborItems: [],
        };
        const hasReal =
          (bd.materialItems?.length || 0) + (bd.laborItems?.length || 0) > 0;
        const sectionTotal = hasReal
          ? (bd.materialTotal || 0) + (bd.laborTotal || 0)
          : (section.materialPrice || 0) + (section.laborPrice || 0);
        const breakdownRows = hasReal
          ? [...bd.materialItems, ...bd.laborItems].map((item, idx) => {
              const qty = Number(item.breakdownQty || 0);
              const up = Number(item.breakdownUnitPrice || 0);
              return {
                n: `${section.number}.${idx + 1}`,
                description:
                  item.breakdownDescription?.trim() ||
                  item.customerDescription?.trim() ||
                  "—",
                qty: qty.toFixed(2),
                unit: item.breakdownUnit || "EA",
                unitPrice: formatCurrencyForPDF(up),
                total: formatCurrencyForPDF(qty * up),
              };
            })
          : [
              {
                n: `${section.number}.1`,
                description: "Material",
                qty: "",
                unit: "",
                unitPrice: "",
                total: formatCurrencyForPDF(section.materialPrice || 0),
              },
              {
                n: `${section.number}.2`,
                description: "Labor",
                qty: "",
                unit: "",
                unitPrice: "",
                total: formatCurrencyForPDF(section.laborPrice || 0),
              },
            ];
        return {
          sn: section.number,
          sectionTitle: section.title?.trim() || `Section ${section.number}`,
          sectionTotal: formatCurrencyForPDF(sectionTotal),
          breakdownRows,
        };
      });
      const subtotal = pricingSections.reduce((sum, s) => {
        const bd = sectionBreakdownTotals[s.number] || {};
        const has =
          (bd.materialItems?.length || 0) + (bd.laborItems?.length || 0) > 0;
        return (
          sum +
          (has
            ? (bd.materialTotal || 0) + (bd.laborTotal || 0)
            : (s.materialPrice || 0) + (s.laborPrice || 0))
        );
      }, 0);
      const totalMaterial = pricingSections.reduce((sum, s) => {
        const bd = sectionBreakdownTotals[s.number] || {};
        return (
          sum +
          ((bd.materialItems?.length || 0) > 0
            ? bd.materialTotal || 0
            : s.materialPrice || 0)
        );
      }, 0);
      const totalLabor = pricingSections.reduce((sum, s) => {
        const bd = sectionBreakdownTotals[s.number] || {};
        return (
          sum +
          ((bd.laborItems?.length || 0) > 0
            ? bd.laborTotal || 0
            : s.laborPrice || 0)
        );
      }, 0);
      const overhead = subtotal * ((formData.overheadPercentage || 0) / 100);
      const data = {
        changeOrderNumber:
          summary?.revision > 0
            ? `${savedChangeOrder?.changeOrderNumber} - R${summary.revision}`
            : savedChangeOrder?.changeOrderNumber || "TBD",
        title: formData.title,
        projectName: project?.projectName || "Unknown Project",
        date: new Intl.DateTimeFormat("en-US", {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        }).format(new Date()),
        attentionTo:
          selectedAttentionTo.length > 0
            ? selectedAttentionTo
                .map((c) => (c.email ? `${c.label} (${c.email})` : c.label))
                .join(", ")
            : project?.customerContact
              ? `${project.customerContact.firstName} ${project.customerContact.lastName} (${project.customerContact.email})`
              : "N/A",
        projectAccount:
          project?.account?.companyName ||
          project?.accountId?.companyName ||
          "Unknown Contractor",
        customerContact: project?.customerContact
          ? `${project.customerContact.firstName} ${project.customerContact.lastName}`
          : "N/A",
        description:
          formData.customerDescription || summary.description || "N/A",
        referenceNumber: formData.referenceNumber || "",
        pricingSections: pricingSectionsData,
        materialSubtotal: formatCurrencyForPDF(totalMaterial),
        labourTotal: formatCurrencyForPDF(totalLabor),
        subtotal: formatCurrencyForPDF(subtotal),
        ohPercent: formData.overheadPercentage.toFixed(2),
        overhead: formatCurrencyForPDF(overhead),
        total: formatCurrencyForPDF(subtotal + overhead),
        changestoSchedule: formData.changesToSchedule,
        termsAndConditions: formData.termsAndConditions || "N/A",
        companyName: companyProfile?.companyName || "Your Company Name",
        companyAddress: companyProfile?.address
          ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
          : "N/A",
        image: imageBase64,
      };
      doc.setData(data);
      doc.render();
      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const formDataPDF = new FormData();
      formDataPDF.append(
        "docx",
        docxBlob,
        `ChangeOrder_${data.changeOrderNumber}.docx`,
      );
      formDataPDF.append("documentType", "ChangeOrder");
      formDataPDF.append("identifier", data.changeOrderNumber);
      const pdfResponse = await axios.post(
        `${API_BASE}/upload/convert-to-pdf`,
        formDataPDF,
        {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
          responseType: "blob",
        },
      );

      // For T&M-based COs, merge in the T&M combined PDF
      const coPdfBlob = new Blob([pdfResponse.data], {
        type: "application/pdf",
      });

      // For T&M-based COs, generate the T&M PDF and merge client-side
      let finalBlob = coPdfBlob;
      if (isTandMBased && tandmId) {
        const tandmBlob = await generateTandMPDFBlob();
        if (tandmBlob) {
          try {
            finalBlob = await mergePDFBlobs(coPdfBlob, tandmBlob);
          } catch (mergeErr) {
            console.error(
              "Client-side PDF merge failed, using CO PDF only:",
              mergeErr,
            );
            finalBlob = coPdfBlob; // non-fatal fallback
          }
        }
      }

      if (forEmail) {
        setPdfBuffer(finalBlob);
        setShowEmailModal(true);
      } else {
        saveAs(finalBlob, `ChangeOrder_${data.changeOrderNumber}.pdf`);
        showAlert(
          "success",
          "Success",
          isTandMBased
            ? "Change Order + T&M PDF generated and merged!"
            : "Change Order PDF generated!",
        );
      }
      return finalBlob;
    } catch (err) {
      console.error("PDF generation error:", err);
      showAlert("error", "Error", `Failed to generate PDF: ${err.message}`);
      return null;
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleSendEmail = async () => {
    if (!userEmail) {
      showAlert("error", "Error", "User email not available.");
      return;
    }
    if (!savedChangeOrder?._id) {
      showAlert(
        "error",
        "Error",
        "Please save the Change Order before sending an email.",
      );
      return;
    }
    await generateChangeOrderPDF(true);
  };

  const handleEmailSubmit = async () => {
    if (!emailForm.to || !emailForm.subject || !emailForm.body) {
      showAlert(
        "error",
        "Invalid Input",
        "Please fill in all required email fields",
      );
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (
      !emailForm.to
        .split(",")
        .map((e) => e.trim())
        .every((e) => emailRegex.test(e))
    ) {
      showAlert("error", "Invalid Email", "Please enter valid email addresses");
      return;
    }
    if (!pdfBuffer || pdfBuffer.type !== "application/pdf") {
      showAlert(
        "error",
        "Missing Attachment",
        "A valid Change Order PDF is required",
      );
      return;
    }
    setIsSendingEmail(true);
    try {
      const fd = new FormData();
      fd.append("to", emailForm.to);
      fd.append("subject", emailForm.subject);
      fd.append("body", emailForm.body);
      fd.append("signature", emailForm.signature || "");
      fd.append("from", userEmail);
      fd.append(
        "files",
        pdfBuffer,
        `ChangeOrder_${savedChangeOrder?.changeOrderNumber || "Pending"}.pdf`,
      );
      const res = await axios.post(
        `${API_BASE}/changeOrders/${changeOrderId}/send-email`,
        fd,
        { headers },
      );
      setShowEmailModal(false);
      setPdfBuffer(null);
      if (res.status === 200)
        showAlert("success", "Success", "Email sent successfully!");
    } catch (err) {
      console.error("Email error:", err);
      showAlert(
        "error",
        "Error",
        `Failed to send email: ${err.response?.data?.message || err.message}`,
      );
    } finally {
      setIsSendingEmail(false);
    }
  };

  // ── Export CO items as Budget-format Excel for V3 upload ──────────────────────
  // ── Export CO items as multi-sheet Budget Excel for V3 upload ─────────────────
  const handleExportForV3 = () => {
    if (!savedChangeOrder || changeItems.length === 0) {
      showAlert("error", "Nothing to export", "Add items before exporting.");
      return;
    }

    const SF_LF_UOMS = [
      "sf",
      "lf",
      "sq ft",
      "lin ft",
      "square",
      "linear",
      "sheet",
    ];
    const isCovUom = (uom) =>
      SF_LF_UOMS.some((u) => (uom || "").toLowerCase().includes(u));

    const HEADERS = [
      "Item #",
      "Category",
      "Section",
      "Mat. Code",
      "Labor Code",
      "Description",
      "Size",
      "Quantity",
      "UoM",
      "Unit Cost",
      "Coverage/Unit",
      "Req Qty",
      "Order As",
      "Material Cost",
      "Mat. Total",
      "Labor Total",
      "Item Total",
      "Hours",
      "Area",
      "Condition/Assembly",
      "Bid Area",
    ];

    const COL_WIDTHS = [
      { wch: 7 },
      { wch: 9 },
      { wch: 10 },
      { wch: 14 },
      { wch: 14 },
      { wch: 42 },
      { wch: 12 },
      { wch: 10 },
      { wch: 7 },
      { wch: 10 },
      { wch: 13 },
      { wch: 9 },
      { wch: 10 },
      { wch: 18 },
      { wch: 11 },
      { wch: 11 },
      { wch: 11 },
      { wch: 7 },
      { wch: 12 },
      { wch: 22 },
      { wch: 14 },
    ];

    const buildRow = (item, itemNum) => {
      const isMat = item.category === "M";
      // deltaQty is signed — negative means deduct. Preserve the sign.
      const deltaQty = item.deltaQty || item.quantity || 0;
      const absQty = Math.abs(deltaQty);
      const uom = item.uom || "";
      const hasCov = isMat && isCovUom(uom);
      const cov = hasCov ? parseFloat(item.coveragePerUnit) || 1 : null;
      const unitCost = isMat
        ? item.unitCost || 0
        : item.unitRate || item.unitCost || 0;

      const reqQty =
        hasCov && cov ? Math.ceil(absQty / cov) : isMat ? absQty : null;

      const matCostStr = isMat
        ? hasCov
          ? `${(unitCost * 1000).toLocaleString("en-US", { minimumFractionDigits: 2 })} /1,000 ${uom}`
          : `${unitCost.toLocaleString("en-US", { minimumFractionDigits: 2 })} /${uom || "EA"}`
        : "-";

      // Totals: calculate from absQty, then apply sign from deltaQty
      const rawMat = isMat
        ? Number((absQty * (cov || 1) * unitCost).toFixed(2))
        : 0;
      const rawLab = !isMat ? Number((absQty * unitCost).toFixed(2)) : 0;
      const matTotal = deltaQty < 0 ? -rawMat : rawMat;
      const labTotal = deltaQty < 0 ? -rawLab : rawLab;

      return [
        itemNum,
        item.category === "S" ? "L" : item.category || "M", // S→L for V3
        item.sectionNumber || "",
        isMat ? item.itemCode || item.materialCode || "" : "-",
        !isMat ? item.itemCode || item.laborCode || "" : "-",
        item.customerDescription || item.itemDescription || item.title || "",
        item.size || "",
        deltaQty, // ← SIGNED quantity (negative = deduct)
        uom,
        unitCost,
        hasCov ? cov : "-",
        reqQty ?? "-",
        reqQty != null ? `${reqQty} ${uom}` : "-",
        matCostStr,
        matTotal,
        labTotal,
        Number((matTotal + labTotal).toFixed(2)),
        0,
        item.bidArea || "",
        item.conditionName || "",
        item.bidArea || "",
      ];
    };

    // Group items by pricing section — one sheet per section
    // Items with no referenceNumber go to "Unassigned" sheet
    const sectionMap = new Map(); // sectionNumber → { label, items[] }

    // Add sheets in section order first
    pricingSections.forEach((s) => {
      sectionMap.set(s.number, {
        label: `${s.number} — ${s.title || "Section " + s.number}`,
        sheetName: `S${s.number} ${s.title || "Section " + s.number}`
          .replace(/[:\\/?\*\[\]]/g, "")
          .slice(0, 31),
        items: [],
      });
    });

    // Bucket with no section
    sectionMap.set(null, {
      label: "Unassigned",
      sheetName: "Unassigned",
      items: [],
    });

    changeItems.forEach((item) => {
      const key = item.referenceNumber ?? null;
      if (sectionMap.has(key)) {
        sectionMap.get(key).items.push(item);
      } else {
        sectionMap.get(null).items.push(item);
      }
    });

    // Build workbook — one sheet per section that has items
    const wb = XLSX.utils.book_new();
    let anySheet = false;

    sectionMap.forEach(({ label, sheetName, items }) => {
      if (items.length === 0) return;
      anySheet = true;

      // Row 1: section label banner
      // Row 2: headers
      // Row 3+: items
      const aoa = [
        [`Pricing Section: ${label}`, ...Array(HEADERS.length - 1).fill("")],
        HEADERS,
        ...items.map((item, idx) => buildRow(item, idx + 1)),
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = COL_WIDTHS;

      // Merge section label across all columns
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: HEADERS.length - 1 } },
      ];

      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    if (!anySheet) {
      showAlert("error", "Nothing to export", "No items to export.");
      return;
    }

    XLSX.writeFile(
      wb,
      `CO_${savedChangeOrder.changeOrderNumber}_v3_export.xlsx`,
    );
  };

  // ── Revision display data ─────────────────────────────────────────────────────
  const revisionDisplayData = useMemo(() => {
    if (!viewingRevision) return null;
    const revItems = (viewingRevision.items || []).map((item, i) => ({
      ...item,
      _localId: `rev-${i}`,
    }));
    const revSections = viewingRevision.pricingSections || [];
    let budgetCost = 0;
    revItems.forEach((item) => {
      if (!item.forBudget) return;
      const qty = Math.abs(item.deltaQty || item.quantity || 0);
      const cov = parseFloat(item.coveragePerUnit) || 1;
      const isCov =
        item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
      const base =
        item.category !== "M" && item.unitRate > 0
          ? item.unitRate
          : item.unitCost || 0;
      const total = isCov ? qty * cov * base : qty * base;
      budgetCost += (item.deltaQty || 0) < 0 ? -total : total;
    });
    let customerSubtotal = 0;
    revSections.forEach((s) => {
      customerSubtotal += (s.materialPrice || 0) + (s.laborPrice || 0);
    });
    const overheadAmount =
      customerSubtotal * ((viewingRevision.overheadPercentage || 0) / 100);
    return {
      items: revItems,
      pricingSections: revSections,
      formData: {
        title: viewingRevision.title || formData.title,
        customerDescription: viewingRevision.customerDescription || "",
        termsAndConditions: viewingRevision.termsAndConditions || "",
        overheadPercentage: viewingRevision.overheadPercentage || 0,
        defaultMarkupPercentage: viewingRevision.defaultMarkupPercentage || 30,
        changesToSchedule: viewingRevision.changesToSchedule || 0,
        approvalStatus: formData.approvalStatus,
      },
      totals: {
        budgetCost: Number(budgetCost.toFixed(2)),
        customerSubtotal: Number(customerSubtotal.toFixed(2)),
        overheadAmount: Number(overheadAmount.toFixed(2)),
        totalCustomerPrice: Number(
          (customerSubtotal + overheadAmount).toFixed(2),
        ),
      },
    };
  }, [viewingRevision, formData.title, formData.approvalStatus]);

  const displayItems = viewingRevision
    ? (viewingRevision.items || []).map((item, i) => ({
        ...item,
        _localId: item._id || `rev-item-${i}`,
      }))
    : sortedAndFilteredItems;
  const displaySections = viewingRevision
    ? viewingRevision.pricingSections || []
    : pricingSections;
  const displayFormData = viewingRevision
    ? {
        ...formData,
        overheadPercentage:
          viewingRevision.overheadPercentage ?? formData.overheadPercentage,
      }
    : formData;
  const displayTotals = viewingRevision
    ? {
        budgetCost: viewingRevision.totalBudgetCost,
        customerSubtotal: viewingRevision.totalCustomerPrice,
        overheadAmount: 0,
        totalCustomerPrice: viewingRevision.totalCustomerPrice,
      }
    : totals;

  // For T&M COs: cost of items the PM added directly in this CO
  // (separate from the locked T&M base cost)
  const newScopeCost = isTandMBased
    ? changeItems.reduce((sum, item) => {
        if (!item.forBudget) return sum;
        const sq = item.deltaQty || item.quantity || 0;
        const qty = Math.abs(sq);
        const cov = parseFloat(item.coveragePerUnit) || 1;
        const isCovItem =
          item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
        const cost =
          item.category !== "M" && item.unitRate > 0
            ? item.unitRate
            : item.unitCost || 0;
        const total = isCovItem ? qty * cov * cost : qty * cost;
        return sum + (sq < 0 ? -total : total);
      }, 0)
    : 0;

  const tandmBaseCost = isTandMBased
    ? tandmCostData?.totals?.totalCost || 0
    : 0;
  // The true total CO cost for T&M COs = T&M base + any additional items
  const tandmTotalCOCost = wasApproved
    ? tandmBaseCost
    : tandmBaseCost + newScopeCost;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-800">
        <div className="text-center py-8 text-gray-200">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4" />
          Loading Change Order...
        </div>
      </div>
    );
  }

  // ── Shared props for sub-components ──────────────────────────────────────────
  const sharedProps = {
    changeItems,
    pricingSections,
    wasApproved,
    isReadOnly,
    formData,
    displayFormData,
    isTandMBased,
    tandmId,
    tandmNumber,
    tandmCostData, // ← ADD THIS LINE
    formatCurrency,
    isSFUnit,
    isLFUnit,
    isFromBudget,
    isTandMSourcedItem: (item) => isTandMBased && !!item.sourceLineItemId,
    isTandMNewItem: (item) => isTandMBased && !item.sourceLineItemId,
    parseSizeToCoverage,
    assemblyOptions,
    bidAreaOptions,
    referenceOptions,
    getLaborIdOptions,
    customStyles,
    handleUpdateItem,
    handleRemoveItem,
    handleMoveItem,
    sectionBreakdownTotals,
    sectionEstimatedCosts,
    totals,
    displayTotals,
    hasActiveFiltersOrSort,
    sortedAndFilteredItems,
    filteredChangeItems,
    displayItems,
    changeOrderFilters,
    setChangeOrderFilters,
    showCOFilter,
    setShowCOFilter,
    coFilterRefs,
    getCOFilterOptions,
    getSortIndicator,
    requestSort,
    addCustomPricingItem,
    updateCustomPricingItem,
    removeCustomPricingItem,
    sectionActiveTab,
    setSectionActiveTab,
    handleSectionAdd,
    hasBreakdownItems,
    getSectionTotal,
    expandedSections,
    toggleSectionExpand,
    addPricingSection,
    removePricingSection,
    updatePricingSection,
    displaySections,
  };

  const renderHeader = () => (
    <div className="mb-6 shadow-lg bg-gray-700 rounded-lg p-6 border border-gray-600">
      {wasApproved && (
        <div className="flex gap-2 border-gray-600 mb-2">
          {["details", "amendments"].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveMainTab(tab)}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${activeMainTab === tab ? "bg-gray-700 text-orange-400 border-b-2 border-orange-400" : "text-gray-400 hover:text-gray-200"}`}
            >
              {tab === "details" ? (
                "Details & Pricing"
              ) : (
                <>
                  Amendments
                  {amendments.filter((a) => a.status === "Pending").length >
                    0 && (
                    <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-orange-500 text-white rounded-full">
                      {amendments.filter((a) => a.status === "Pending").length}
                    </span>
                  )}
                </>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-semibold text-white">
            Edit Change Quotation #{savedChangeOrder?.changeOrderNumber || ""}
          </h1>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${formData.approvalStatus === "Approved" ? "bg-green-600 text-white" : formData.approvalStatus === "Rejected" ? "bg-red-600 text-white" : "bg-yellow-600 text-white"}`}
          >
            {formData.approvalStatus}
          </span>
          {isTandMBased && (
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-teal-800/60 text-teal-300 border border-teal-600/30 flex items-center gap-1.5">
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 01-2.828-2.828l3-3z"
                  clipRule="evenodd"
                />
              </svg>
              T&M {tandmNumber ? `#${tandmNumber}` : "Linked"}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() =>
            navigate(`/projects/${projectId}`, {
              state: { initialTab: "Change Order (CO)" },
            })
          }
          className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded hover:bg-gray-500 flex items-center"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back
        </button>
      </div>
      <div className="mt-2">
        <CORevisionPanel
          changeOrderId={changeOrderId}
          isApproved={wasApproved}
          activeRevisionIndex={savedChangeOrder?.activeRevisionIndex ?? null}
          onRevisionCreated={() => {}}
          onRevisionRestored={() => window.location.reload()}
          onViewRevision={(rev) => setViewingRevision(rev)}
          showAlert={showAlert}
          token={token}
        />
      </div>
      {wasApproved && (
        <div className="mt-4 bg-green-900/30 border-l-4 border-green-500 text-green-300 p-3 rounded">
          <p className="text-sm">
            This Change Order is <strong>Approved</strong>.{" "}
            {isTandMBased
              ? "T&M cost is locked. You may add new scope items — they will be added to the T&M budget on approval. "
              : "Budget items are locked. "}
            Only pricing sections, overhead percentage, and billable status can
            be modified.
          </p>
        </div>
      )}
    </div>
  );

  // ── Amendments tab ────────────────────────────────────────────────────────────
  if (wasApproved && activeMainTab === "amendments") {
    return (
      <div className="max-w-full mx-auto px-4 py-6">
        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert((p) => ({ ...p, open: false }))}
          onConfirm={alert.onConfirm}
        />
        <div className="mb-6 shadow-lg bg-gray-700 rounded-lg p-6 border border-gray-600">
          {renderHeader()}
          <COAmendmentPanel
            changeOrder={savedChangeOrder}
            amendments={amendments}
            coItems={cumulativeCOItems || []} // ← Change from changeItems
            netQtyMap={netQtyMap}
            netQuantities={netQuantities}
            budgetItems={budgetItems}
            databaseItems={databaseItems}
            loadingDatabase={loadingDatabaseItems}
            vendors={vendors}
            ExcelUploadModal={EditChangeOrderExcelUploadModal}
            projectId={projectId}
            token={token}
            showAlert={showAlert}
            onAmendmentsChange={(updated) => setAmendments(updated)}
            fetchNetQuantities={fetchNetQuantities}
            fetchDatabaseItems={fetchDatabaseItems}
          />
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────────
  return (
    <div className="max-w-full mx-auto px-4 py-6">
      {viewingRevision && (
        <div className="mb-4 bg-gray-700 border border-indigo-500/60 rounded-lg px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-white text-sm font-semibold">
              👁 Viewing Snapshot:{" "}
              {viewingRevision.label || `R${viewingRevision.revisionNumber}`}
            </span>
            <span className="text-white text-xs">
              {new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
              }).format(new Date(viewingRevision.createdAt))}
            </span>
            {viewingRevision.note && (
              <span className="text-white text-xs italic">
                — {viewingRevision.note}
              </span>
            )}
            <span className="text-xs text-white bg-orange-600 px-2 py-0.5 rounded">
              Read-only
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => generateChangeOrderPDF(false)}
              disabled={isGeneratingPDF}
              className="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            >
              <DocumentArrowDownIcon className="h-3.5 w-3.5" />
              {isGeneratingPDF ? "Generating..." : "Download PDF"}
            </button>

            <button
              type="button"
              onClick={() => setViewingRevision(null)}
              className="px-3 py-1.5 text-xs text-white bg-gray-600 rounded hover:bg-gray-500 flex items-center gap-1"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
              Back to Live View
            </button>
          </div>
        </div>
      )}

      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert((p) => ({ ...p, open: false }))}
        onConfirm={alert.onConfirm}
      />
      <ConfirmationModal
        open={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={handleApprove}
        title="Approve Change Order"
        message={
          isTandMBased
            ? "Approving this T&M-based CO will lock pricing. The cost base remains in the linked T&M."
            : "Approving will create a variance budget. This cannot be easily undone."
        }
        confirmText="Approve"
        confirmColor="green"
      />
      <ConfirmationModal
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={handleReject}
        title="Reject Change Order"
        message={
          wasApproved
            ? "Rejecting this approved CO will remove the variance budget."
            : "Are you sure you want to reject this change order?"
        }
        confirmText="Reject"
        confirmColor="red"
      />
      <ConfirmationModal
        open={showUnlinkConfirm}
        onClose={() => setShowUnlinkConfirm(false)}
        onConfirm={handleUnlinkTandM}
        title="Unlink T&M"
        message="This will remove the T&M cost link. The CO will become a standard CO. The T&M will become available to link to another CO. This cannot be undone after approval."
        confirmText="Unlink T&M"
        confirmColor="red"
      />
      <EmailModal
        open={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setPdfBuffer(null);
        }}
        emailForm={emailForm}
        setEmailForm={setEmailForm}
        onSend={handleEmailSubmit}
        userEmail={userEmail}
        isSending={isSendingEmail}
        contacts={contacts}
        loadingContacts={loadingContacts}
      />
      <BudgetItemsModal
        open={showBudgetModal}
        onClose={() => {
          setShowBudgetModal(false);
          setSelectedBudgetItems([]);
        }}
        existingChangeItems={changeItems}
        budgetItems={budgetItems}
        loading={loadingBudgetItems}
        selectedItems={selectedBudgetItems}
        setSelectedItems={setSelectedBudgetItems}
        onAddItems={() => addBudgetItemsToChangeOrder(addingToSection)}
        formatCurrency={formatCurrency}
        getFilterOptions={getFilterOptions}
        netQuantities={netQuantities}
        netLoading={netLoading}
      />
      <DatabaseItemsModal
        open={showDatabaseModal}
        onClose={() => {
          setShowDatabaseModal(false);
          setSelectedDatabaseItems([]);
          setDatabaseFilters({ category: [], uom: [], search: "" });
        }}
        databaseItems={databaseItems}
        loading={loadingDatabaseItems}
        databaseFilters={databaseFilters}
        setDatabaseFilters={setDatabaseFilters}
        selectedDatabaseItems={selectedDatabaseItems}
        setSelectedDatabaseItems={setSelectedDatabaseItems}
        onAddItems={() => addDatabaseItemsToChangeOrder(addingToSection)}
        formatCurrency={formatCurrency}
        formData={formData}
        parseSizeToCoverage={parseSizeToCoverage}
        isSFUnit={isSFUnit}
        isLFUnit={isLFUnit}
        getDatabaseFilterOptions={getDatabaseFilterOptions}
        netQuantities={netQuantities}
        netLoading={netLoading}
      />
      <EditChangeOrderExcelUploadModal
        open={showExcelUploadModal}
        onClose={() => setShowExcelUploadModal(false)}
        onAddItems={(items) => addExcelUploadedItems(items, addingToSection)}
        databaseItems={databaseItems}
        vendors={vendors}
        loadingDatabase={loadingDatabaseItems}
        existingChangeItems={changeItems}
        defaultMarkupPercentage={formData.defaultMarkupPercentage}
        budgetItems={budgetItems}
        netQuantities={netQuantities}
        netLoading={netLoading}
        projectId={projectId}
      />

      <form onSubmit={handleSave} className="space-y-6">
        {renderHeader()}

        {/* Details */}
        <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
          <h2 className="text-lg font-semibold text-white mb-3">
            Change Order Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-400">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
                required
                disabled={wasApproved}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Client Ref #
              </label>
              <input
                type="text"
                name="referenceNumber"
                value={formData.referenceNumber}
                onChange={handleFormChange}
                placeholder="e.g., PCO-001"
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Overhead %
              </label>
              <input
                type="number"
                name="overheadPercentage"
                value={formData.overheadPercentage}
                onChange={handleFormChange}
                step="0.01"
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Markup %
              </label>
              <input
                type="number"
                name="defaultMarkupPercentage"
                value={formData.defaultMarkupPercentage}
                onChange={handleFormChange}
                step="0.01"
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
                disabled={wasApproved}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400">
                Schedule (Days)
              </label>
              <input
                type="number"
                name="changesToSchedule"
                value={formData.changesToSchedule}
                onChange={handleFormChange}
                className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
                disabled={wasApproved}
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Attention To
            </label>
            <Select
              isMulti
              options={contacts.map((c) => ({
                value: c._id,
                label:
                  `${c.firstName || ""} ${c.lastName || ""}`.trim() ||
                  "Unnamed",
                email: c.email || "",
              }))}
              value={selectedAttentionTo}
              onChange={(s) => setSelectedAttentionTo(s || [])}
              placeholder={
                loadingContacts
                  ? "Loading contacts..."
                  : contacts.length === 0
                    ? "No contacts available"
                    : "Select contacts..."
              }
              isLoading={loadingContacts}
              className="w-full"
              styles={customStyles}
              menuPortalTarget={document.body}
              noOptionsMessage={() => "No contacts found for this account"}
              formatOptionLabel={(o) => (
                <div className="flex justify-between items-center">
                  <span>{o.label}</span>
                  {o.email && (
                    <span className="text-xs text-gray-400 ml-2">
                      {o.email}
                    </span>
                  )}
                </div>
              )}
            />
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-400">
              Customer Description
            </label>
            <textarea
              name="customerDescription"
              value={formData.customerDescription}
              onChange={handleFormChange}
              rows="2"
              className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
              disabled={wasApproved}
            />
          </div>
        </div>

        {/* Budget Items Section */}
        <COBudgetItemsSection
          {...sharedProps}
          budgetItemsCollapsed={budgetItemsCollapsed}
          setBudgetItemsCollapsed={setBudgetItemsCollapsed}
          setShowBudgetModal={setShowBudgetModal}
          setShowDatabaseModal={setShowDatabaseModal}
          setShowExcelUploadModal={setShowExcelUploadModal}
          setAddingToSection={setAddingToSection}
          tandmCostData={tandmCostData}
          setTandmCostData={setTandmCostData}
          setShowUnlinkConfirm={setShowUnlinkConfirm}
          projectId={projectId}
          token={token}
        />

        {/* Pricing Sections */}
        <COPricingSections {...sharedProps} viewingRevision={viewingRevision} />

        {/* Summary */}
        <div className="bg-gray-700 p-4 rounded-lg border border-gray-600">
          <h2 className="text-lg font-semibold text-white mb-4">Summary</h2>
          {isTandMBased && !viewingRevision && (
            <div className="mb-4 rounded-lg border border-teal-600/30 bg-teal-900/10 px-4 py-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <svg
                    className="h-4 w-4 text-teal-400 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 01-2.828-2.828l3-3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium text-teal-300">
                    T&M #{tandmNumber || "Linked"} Cost Breakdown
                  </span>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">T&M Base</span>
                    <span className="font-semibold text-teal-400 tabular-nums">
                      {formatCurrency(tandmBaseCost)}
                    </span>
                  </div>
                  {newScopeCost !== 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs">
                        + Additional Items
                      </span>
                      <span className="font-semibold text-orange-400 tabular-nums">
                        {formatCurrency(newScopeCost)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 pl-3 border-l border-gray-600">
                    <span className="text-gray-400 text-xs">Total CO Cost</span>
                    <span className="font-bold text-white tabular-nums text-base">
                      {formatCurrency(tandmTotalCOCost)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
            {[
              {
                label: isTandMBased ? "Total CO Cost" : "Budget Cost",
                // For T&M COs use tandmTotalCOCost (T&M base + additional items)
                // rather than displayTotals.budgetCost which may not include T&M base
                // if tandmCostData hasn't loaded yet or totals memo hasn't updated.
                value: isTandMBased
                  ? tandmTotalCOCost
                  : displayTotals.budgetCost,
                color:
                  (isTandMBased
                    ? tandmTotalCOCost
                    : displayTotals.budgetCost) >= 0
                    ? "text-green-400"
                    : "text-red-400",
              },
              {
                label: "Material Total",
                value: totals.totalMaterialPrice,
                color: "text-blue-400",
              },
              {
                label: "Labor Total",
                value: totals.totalLaborPrice,
                color: "text-purple-400",
              },
              {
                label: "Subtotal",
                value: displayTotals.customerSubtotal,
                color: "text-gray-200",
              },
              {
                label: `Overhead (${displayFormData.overheadPercentage}%)`,
                value: displayTotals.overheadAmount,
                color: "text-gray-200",
              },
              {
                label: "Customer Total",
                value: displayTotals.totalCustomerPrice,
                color: "text-orange-400",
              },
              {
                label: "Gross Margin",
                value: null,
                extra: (
                  <p
                    className={`text-lg font-semibold ${totals.margin >= 0 ? "text-green-400" : "text-red-400"}`}
                  >
                    {totals.marginPercent}%
                  </p>
                ),
              },
            ].map(({ label, value, color, extra }) => (
              <div key={label} className="bg-gray-600 p-3 rounded">
                <p className="text-xs text-gray-400">{label}</p>
                {extra || (
                  <p className={`text-lg font-semibold ${color}`}>
                    {formatCurrency(value)}
                  </p>
                )}
              </div>
            ))}
          </div>
          {!viewingRevision && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Internal Notes
                </label>
                <textarea
                  value={summary.description}
                  onChange={(e) =>
                    setSummary((p) => ({ ...p, description: e.target.value }))
                  }
                  rows="2"
                  className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400">
                  Terms and Conditions
                </label>
                <textarea
                  name="termsAndConditions"
                  value={formData.termsAndConditions}
                  onChange={handleFormChange}
                  rows="2"
                  className="mt-1 w-full rounded bg-gray-600 border-gray-500 text-gray-200 text-sm"
                  disabled={wasApproved}
                />
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-start gap-6">
            <div className="flex items-center">
              <span className="text-sm text-gray-400 mr-2">Billable:</span>
              <button
                type="button"
                onClick={() =>
                  setSummary((p) => ({
                    ...p,
                    billable: !p.billable,
                    nonBillableReason: p.billable ? p.nonBillableReason : "",
                  }))
                }
                className={`relative w-16 h-7 rounded-full ${summary.billable ? "bg-orange-500" : "bg-gray-500"}`}
              >
                <span
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform ${summary.billable ? "translate-x-1" : "-translate-x-6"}`}
                />
              </button>
              <span className="ml-2 text-sm text-gray-300">
                {summary.billable ? "Yes" : "No"}
              </span>
            </div>
            {!summary.billable && (
              <div className="flex-1 min-w-[280px]">
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Non-Billable Reason <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-2">
                  <select
                    value={
                      [
                        "Site Condition Change",
                        "Foreman Field Decision",
                        "Estimation Error",
                        "Design / Engineering Change",
                        "Other",
                      ].includes(summary.nonBillableReason)
                        ? summary.nonBillableReason
                        : summary.nonBillableReason
                          ? "Other"
                          : ""
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      setSummary((p) => ({
                        ...p,
                        nonBillableReason: v === "Other" ? "" : v,
                        _nonBillableCustom: v === "Other",
                      }));
                    }}
                    className="rounded bg-gray-600 border-gray-500 text-gray-200 text-sm px-3 py-1.5"
                  >
                    <option value="">— Select reason category —</option>
                    {[
                      "Site Condition Change",
                      "Foreman Field Decision",
                      "Estimation Error",
                      "Design / Engineering Change",
                    ].map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                    <option value="Other">Other (specify)...</option>
                  </select>
                  {(summary._nonBillableCustom ||
                    (summary.nonBillableReason &&
                      ![
                        "Site Condition Change",
                        "Foreman Field Decision",
                        "Estimation Error",
                        "Design / Engineering Change",
                        "Other",
                      ].includes(summary.nonBillableReason))) && (
                    <input
                      type="text"
                      value={summary.nonBillableReason}
                      onChange={(e) =>
                        setSummary((p) => ({
                          ...p,
                          nonBillableReason: e.target.value,
                        }))
                      }
                      placeholder="Describe reason..."
                      className="flex-1 rounded bg-gray-600 border-gray-500 text-gray-200 text-sm px-3 py-1.5"
                      autoFocus
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          {!viewingRevision ? (
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={() =>
                  navigate(`/projects/${projectId}`, {
                    state: { initialTab: "Change Order (CO)" },
                  })
                }
                className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => generateChangeOrderPDF(false)}
                disabled={isGeneratingPDF || !savedChangeOrder}
                className={`px-4 py-2 text-sm text-white rounded flex items-center ${isGeneratingPDF || !savedChangeOrder ? "bg-gray-500 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                {isGeneratingPDF ? "Generating..." : "Download PDF"}
              </button>
              <button
                type="button"
                onClick={handleExportForV3}
                disabled={!savedChangeOrder || changeItems.length === 0}
                className={`px-4 py-2 text-sm text-white rounded flex items-center ${
                  !savedChangeOrder || changeItems.length === 0
                    ? "bg-gray-500 cursor-not-allowed"
                    : "bg-violet-600 hover:bg-violet-700"
                }`}
              >
                <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                Export for V3
              </button>
              <button
                type="button"
                onClick={handleSendEmail}
                disabled={isGeneratingPDF || !savedChangeOrder}
                className={`px-4 py-2 text-sm text-white rounded flex items-center ${isGeneratingPDF || !savedChangeOrder ? "bg-gray-500 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
              >
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                Send Email
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`px-6 py-2 text-sm text-white rounded ${isSaving ? "bg-gray-500 cursor-not-allowed" : "bg-orange-500 hover:bg-orange-600"}`}
              >
                {isSaving
                  ? "Saving..."
                  : wasApproved
                    ? "Update Pricing"
                    : "Save Changes"}
              </button>
              {isPending && !wasApproved && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isTandMBased) {
                        const missing = changeItems.filter(
                          (i) => !i.bidArea?.trim() || !i.conditionName?.trim(),
                        );
                        if (missing.length > 0) {
                          showAlert(
                            "error",
                            "Missing Required Fields",
                            `${missing.length} item(s) missing Bid Area or Assembly. Fill in both before approving.`,
                          );
                          return;
                        }
                      }
                      setShowApproveModal(true);
                    }}
                    disabled={
                      isSaving ||
                      isApproving ||
                      (!isTandMBased && changeItems.length === 0)
                    }
                    className={`px-6 py-2 text-sm text-white rounded flex items-center ${isSaving || isApproving || (!isTandMBased && changeItems.length === 0) ? "bg-gray-500 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    {isApproving ? "Approving..." : "Approve"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRejectModal(true)}
                    disabled={isSaving}
                    className={`px-6 py-2 text-sm text-white rounded flex items-center ${isSaving ? "bg-gray-500 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"}`}
                  >
                    <XCircleIcon className="h-4 w-4 mr-2" />
                    Reject
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setViewingRevision(null)}
                className="px-4 py-2 text-sm text-gray-200 bg-gray-600 rounded hover:bg-gray-500 flex items-center gap-1"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to Live View
              </button>
              <button
                type="button"
                onClick={() => generateChangeOrderPDF(false)}
                disabled={isGeneratingPDF}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                {isGeneratingPDF ? "Generating..." : "Download Snapshot PDF"}
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
